import type {
  SourceEntityMapping,
  SourceFieldMapping,
  SourceIntent,
  SourceMappingProfile,
  SourceMappingProfileInput,
  SourceTombstonePolicy,
} from '../models';
import { SourceMappingProfileSchema } from '../models';

export interface SpreadsheetIntentResolverInput {
  readonly sourceSystem?: string;
  readonly folderKind?: string;
  readonly profileId?: string;
  readonly headers: readonly string[];
  readonly sampleRows?: readonly Record<string, unknown>[];
  readonly maxCandidates?: number;
  /**
   * Domain-registered fields that may be preserved in addition to the
   * structural intent mapping. Callers must exclude compatibility-only or
   * otherwise unregistered aliases.
   */
  readonly importableFieldAliases?: Readonly<
    Record<string, readonly string[]>
  >;
  /**
   * Entity refs each registered field may be filed on (e.g. `fte` on a
   * person or a position). Seat-per-row intents route such fields to the
   * Position record; fields not listed default to the person.
   */
  readonly importableFieldEntityRefs?: Readonly<
    Record<string, readonly string[]>
  >;
}

export interface SpreadsheetIntentCandidate {
  readonly intent: SourceIntent;
  readonly label: string;
  readonly confidence: number;
  readonly targetFolderKind: string;
  readonly targetProfileId: string;
  readonly reasons: readonly string[];
  readonly missingRecommendedFields: readonly string[];
  /**
   * User-facing semantic fields for the mapping review step. These are the
   * resolver's suggestions only; canonical materialization must use a profile
   * rebuilt from the user's confirmed selections.
   */
  readonly reviewFields: readonly SpreadsheetMappingReviewField[];
  /** Registered source fields available when a reviewed semantic selection changes. */
  readonly registeredFields: readonly SpreadsheetPreservedField[];
  /** Every registered target field available for explicit manual mapping. */
  readonly availableRegisteredFields: readonly string[];
  /** Registered fields added beyond this candidate's semantic mapping. */
  readonly preservedFields: readonly SpreadsheetPreservedField[];
  /**
   * Entity mapping id (person, position, …) each registered target field is
   * filed on for this candidate. Drives the review labels and the reviewed
   * profile, so a manual mapping lands on the same entity the suggestion did.
   */
  readonly registeredFieldEntityRefs: Readonly<Record<string, string>>;
  readonly unrecognizedHeaders: readonly string[];
  /**
   * Structural problems detected in the built profile. Non-empty forces the
   * mapping review step regardless of confidence.
   */
  readonly invariantViolations: readonly string[];
  readonly mappingProfile: SourceMappingProfile;
}

export interface SpreadsheetPreservedField {
  readonly sourceField: string;
  readonly targetField: string;
  /** Entity mapping id the field is filed on; absent means the person. */
  readonly entityRef?: string;
}

const DEFAULT_PRESERVED_FIELD_ENTITY = 'person';

export interface SpreadsheetMappingReviewField {
  readonly id: string;
  readonly label: string;
  readonly sourceField: string | null;
  readonly required: boolean;
}

export type SpreadsheetMappingReviewSelection =
  Readonly<Record<string, string | null>>;

const REGISTERED_FIELD_SELECTION_PREFIX = 'registered:';

export function registeredFieldSelectionId(targetField: string): string {
  return `${REGISTERED_FIELD_SELECTION_PREFIX}${targetField}`;
}

export interface SpreadsheetIntentResolution {
  readonly sourceSystem: string;
  readonly candidates: readonly SpreadsheetIntentCandidate[];
  readonly recommended: SpreadsheetIntentCandidate;
  readonly requiresUserConfirmation: boolean;
  /**
   * The runner-up scored close behind the recommendation. Distinct from the
   * other confirmation triggers: here the resolver *did* recognise the shape
   * and merely had a close second, so the review UI can default to the top
   * pick and carry the doubt in a banner rather than showing a blank slate.
   */
  readonly hasNearTie: boolean;
}

interface IntentSpec {
  readonly intent: SourceIntent;
  readonly label: string;
  readonly targetFolderKind: string;
  readonly targetProfileId: string;
  readonly evidenceMode: 'row_snapshot' | 'source_identity';
  readonly groups: readonly HeaderGroup[];
  readonly folderBias?: Readonly<Record<string, number>>;
  readonly buildProfile: (
    input: BuildProfileInput,
  ) => SourceMappingProfile;
}

interface HeaderGroup {
  readonly id: string;
  readonly label: string;
  readonly patterns: readonly string[];
  readonly weight: number;
  readonly recommended?: boolean;
  readonly exact?: boolean;
}

interface HeaderMatch {
  readonly group: HeaderGroup;
  readonly field: string | null;
}

interface BuildProfileInput {
  readonly spec: IntentSpec;
  readonly sourceSystem: string;
  readonly confidence: number;
  readonly headers: readonly NormalizedHeader[];
  readonly matches: readonly HeaderMatch[];
}

const FIELD_PATTERNS = {
  person: ['person', 'employee', 'name', 'full name', 'worker', 'member'],
  personId: ['person id', 'employee id', 'employee number', 'worker id', 'staff id'],
  email: ['email', 'mail', 'user principal name', 'upn'],
  manager: ['manager', 'reports to', 'supervisor', 'line manager'],
  managerId: ['manager id', 'manager employee id', 'supervisor id'],
  secondaryManager: [
    'secondary reports to', 'secondary manager', 'secondary supervisor',
    'dotted line manager', 'functional manager', 'alternate manager', 'matrix manager',
  ],
  secondaryManagerId: [
    'secondary manager id', 'secondary manager employee id', 'secondary supervisor id',
    'dotted line manager id', 'functional manager id', 'alternate manager id', 'matrix manager id',
  ],
  positionId: ['position id', 'position code', 'job position id', 'seat id'],
  managerPositionId: ['manager position id', 'parent position id', 'reports to position id'],
  title: ['title', 'job title', 'role title', 'position'],
  department: ['department', 'division', 'team', 'org unit', 'business unit'],
  // An HRIS export carries the leaving date as a column on the person row, not
  // as a separate leavers file — so this rides on the existing person intents
  // rather than needing its own. Once imported it is a real graph event:
  // `resolvePendingDepartures` turns it into a seat that can be opened.
  departureDate: [
    'departure date', 'leaving date', 'leave date', 'last day', 'end date',
    'termination date', 'exit date', 'employment end', 'employment end date',
    'separation date',
  ],
  project: ['project', 'initiative', 'program', 'workstream', 'engagement'],
  target: ['target', 'target key', 'work context', 'context', 'objective', 'process', 'service', 'queue', 'control'],
  targetKind: ['target kind', 'target type', 'work context type', 'context type', 'entity type'],
  assignmentRole: ['assignment role', 'staffing role', 'capacity role', 'work role'],
  membershipRole: ['team role', 'project role', 'membership role', 'role in team'],
  membershipType: ['membership type', 'member type', 'membership status'],
  allocation: ['allocation', 'allocated', 'fte', '%', 'percent', 'capacity'],
  requiredFte: ['required fte', 'demand fte', 'needed fte', 'headcount demand', 'required capacity'],
  effort: ['effort', 'hours', 'days', 'estimate'],
  effortUnit: ['effort unit', 'unit', 'hours per week', 'days per week'],
  start: ['start', 'start date', 'from', 'valid from', 'begin'],
  end: ['end', 'end date', 'to', 'valid to', 'finish'],
  basis: ['basis', 'planned', 'actual', 'estimate type', 'scenario'],
  commitmentMode: ['commitment mode', 'allocation mode', 'aggregate or distributed'],
  issue: ['issue', 'ticket', 'key', 'issue key', 'task id', 'work item'],
  summary: ['summary', 'title', 'subject', 'task', 'story'],
  assignee: ['assignee', 'owner', 'responsible', 'assigned to'],
  status: ['status', 'state', 'workflow'],
  sprint: ['sprint', 'iteration', 'milestone', 'release'],
  epic: ['epic', 'parent', 'initiative'],
  process: ['process', 'procedure', 'workflow'],
  activity: ['activity', 'step', 'task', 'control activity'],
  raci: ['raci', 'responsible', 'accountable', 'consulted', 'informed'],
  control: ['control', 'control id', 'control name', 'control identifier'],
  risk: ['risk', 'risk id', 'risk name', 'threat'],
  framework: ['framework', 'clause', 'requirement', 'obligation'],
  evidence: ['evidence', 'artifact', 'proof', 'test result'],
  finding: ['finding', 'issue', 'deficiency', 'remediation'],
  application: ['application', 'app', 'system', 'service', 'resource'],
  permission: ['permission', 'entitlement', 'access', 'privilege', 'role'],
  group: ['group', 'role', 'profile', 'permission set'],
  resource: ['resource', 'arn', 'asset', 'instance', 'node'],
  service: ['service', 'provider', 'cloud service'],
  component: ['component', 'class', 'module', 'api', 'endpoint'],
  database: ['database', 'table', 'schema', 'column'],
  skillId: ['skill id', 'skill code', 'competency id', 'capability id'],
  skill: ['skill', 'skill name', 'competency', 'capability'],
  proficiency: ['proficiency', 'proficiency level', 'skill level', 'rating'],
  proficiencyScale: ['proficiency scale', 'skill scale', 'rating scale', 'scale id'],
  taxonomy: ['taxonomy', 'taxonomy id', 'catalog', 'framework id'],
  taxonomyVersion: ['taxonomy version', 'catalog version', 'framework version'],
  credential: ['credential', 'credential name', 'certification', 'certificate', 'license'],
  credentialId: ['credential id', 'certificate id', 'certification id', 'license number'],
  issuer: ['issuer', 'issuing organization', 'awarding body'],
  issuedAt: ['issued at', 'issue date', 'awarded date'],
  expiresAt: ['expires at', 'expiry date', 'expiration date', 'valid until'],
  assessedAt: ['assessed at', 'assessment date', 'verified at', 'observed at'],
  verificationStatus: ['verification status', 'verified', 'credential status'],
  required: ['required', 'mandatory', 'requirement status'],
  talentDimension: ['talent dimension', 'dimension', 'measure', 'assessment type'],
  observedValue: ['observed value', 'rating value', 'assessment value', 'talent rating'],
  normalizedBand: ['normalized band', 'reviewed band'],
  scaleVersion: ['scale version', 'rating scale version'],
  cycleId: ['cycle id', 'review cycle', 'talent cycle', 'assessment cycle'],
  supersedesRef: ['supersedes', 'supersedes id', 'prior observation id'],
  sourceObservationId: ['observation id', 'assessment id', 'rating id'],
  successor: ['ready now successor', 'successor', 'nominated successor', 'successor name'],
  successorId: ['ready now successor id', 'successor employee id', 'successor id', 'nominated successor id'],
  successionReadiness: ['successor readiness', 'readiness', 'readiness level'],
  successionRank: ['successor rank', 'nomination rank', 'rank'],
} as const;

const ASSIGNEE_PATTERNS = [
  ...FIELD_PATTERNS.assignee,
  'role',
  'owner',
  'person',
  'team',
] as const;

const ACTOR_EMAIL_SUBJECT_HINTS = [
  'actor',
  'assignee',
  'assignedto',
  'person',
  'employee',
  'worker',
  'member',
  'owner',
  'responsible',
  'user',
] as const;

const DIRECT_EMAIL_HEADERS = [
  'email',
  'mail',
  'upn',
  'userprincipalname',
] as const;

const INTENT_SPECS: readonly IntentSpec[] = [
  {
    intent: 'project_allocation',
    label: 'Project allocations',
    targetFolderKind: 'orgchart',
    targetProfileId: 'orgchart',
    evidenceMode: 'row_snapshot',
    groups: [
      group('actor', 'person or email', [...FIELD_PATTERNS.person, ...FIELD_PATTERNS.email], 0.23, true),
      group('project', 'project or initiative', FIELD_PATTERNS.project, 0.23, true),
      group('allocation', 'allocation or effort', [...FIELD_PATTERNS.allocation, ...FIELD_PATTERNS.effort], 0.22, true),
      group('start', 'start date', FIELD_PATTERNS.start, 0.08),
      group('end', 'end date', FIELD_PATTERNS.end, 0.08),
      group('basis', 'basis', FIELD_PATTERNS.basis, 0.05),
    ],
    folderBias: { orgchart: 0.08 },
    buildProfile: buildProjectAllocationProfile,
  },
  {
    intent: 'capacity_commitment',
    label: 'Capacity commitments',
    targetFolderKind: 'orgchart',
    targetProfileId: 'orgchart',
    evidenceMode: 'row_snapshot',
    groups: [
      group('actor', 'person, role, or team', [...FIELD_PATTERNS.person, ...FIELD_PATTERNS.email, 'actor', 'actor key'], 0.2, true),
      group('target', 'target or work context', FIELD_PATTERNS.target, 0.2, true),
      group('targetKind', 'target kind', FIELD_PATTERNS.targetKind, 0.14, true),
      group('allocation', 'allocation', FIELD_PATTERNS.allocation, 0.16),
      group('effort', 'effort', FIELD_PATTERNS.effort, 0.1),
      group('effortUnit', 'effort unit', FIELD_PATTERNS.effortUnit, 0.05),
      group('role', 'role', FIELD_PATTERNS.assignmentRole, 0.05),
      group('start', 'start date', FIELD_PATTERNS.start, 0.05),
      group('end', 'end date', FIELD_PATTERNS.end, 0.05),
      group('basis', 'basis', FIELD_PATTERNS.basis, 0.04),
      group('commitmentMode', 'commitment mode', FIELD_PATTERNS.commitmentMode, 0.03),
    ],
    folderBias: { orgchart: 0.05, strategy: 0.02, process: 0.02 },
    buildProfile: buildCapacityCommitmentProfile,
  },
  {
    intent: 'team_membership',
    label: 'Team or project memberships',
    targetFolderKind: 'orgchart',
    targetProfileId: 'orgchart',
    evidenceMode: 'row_snapshot',
    groups: [
      group('actor', 'person or position', [...FIELD_PATTERNS.person, ...FIELD_PATTERNS.email, ...FIELD_PATTERNS.positionId], 0.24, true),
      exactGroup('team', 'team or project', ['team', 'team name', 'project team', 'work group'], 0.24, true),
      group('role', 'membership role', FIELD_PATTERNS.membershipRole, 0.18, true),
      group('membershipType', 'membership type', FIELD_PATTERNS.membershipType, 0.1),
      group('start', 'start date', FIELD_PATTERNS.start, 0.08),
      group('end', 'end date', FIELD_PATTERNS.end, 0.08),
    ],
    folderBias: { orgchart: 0.08 },
    buildProfile: buildTeamMembershipProfile,
  },
  {
    intent: 'workforce_demand',
    label: 'Workforce demand',
    targetFolderKind: 'orgchart',
    targetProfileId: 'orgchart',
    evidenceMode: 'row_snapshot',
    groups: [
      group('target', 'team, project, or position', [...FIELD_PATTERNS.target, ...FIELD_PATTERNS.project, ...FIELD_PATTERNS.positionId], 0.22, true),
      exactGroup('requiredFte', 'required FTE', FIELD_PATTERNS.requiredFte, 0.3, true),
      group('role', 'required role', ['required role', 'role profile', 'skill profile'], 0.18, true),
      group('start', 'start date', FIELD_PATTERNS.start, 0.08),
      group('end', 'end date', FIELD_PATTERNS.end, 0.08),
      group('basis', 'basis', FIELD_PATTERNS.basis, 0.05),
    ],
    folderBias: { orgchart: 0.08 },
    buildProfile: buildWorkforceDemandProfile,
  },
  {
    intent: 'skill_catalog',
    label: 'Skill catalog',
    targetFolderKind: 'orgchart',
    targetProfileId: 'orgchart',
    evidenceMode: 'source_identity',
    groups: [
      exactGroup('skillId', 'stable skill ID', FIELD_PATTERNS.skillId, 0.34, true),
      exactGroup('skill', 'skill label', FIELD_PATTERNS.skill, 0.28, true),
      group('taxonomy', 'taxonomy', FIELD_PATTERNS.taxonomy, 0.12),
      group('taxonomyVersion', 'taxonomy version', FIELD_PATTERNS.taxonomyVersion, 0.1),
    ],
    folderBias: { orgchart: 0.06 },
    buildProfile: buildSkillCatalogProfile,
  },
  {
    intent: 'skill_assertion',
    label: 'Skill assertions',
    targetFolderKind: 'orgchart',
    targetProfileId: 'orgchart',
    evidenceMode: 'row_snapshot',
    groups: [
      group('actor', 'person or position', [...FIELD_PATTERNS.person, ...FIELD_PATTERNS.email, ...FIELD_PATTERNS.positionId], 0.24, true),
      group('skill', 'skill', [...FIELD_PATTERNS.skillId, ...FIELD_PATTERNS.skill], 0.24, true),
      group('proficiency', 'source proficiency', FIELD_PATTERNS.proficiency, 0.18, true),
      group('proficiencyScale', 'source scale', FIELD_PATTERNS.proficiencyScale, 0.1),
      group('evidence', 'evidence', FIELD_PATTERNS.evidence, 0.06),
      group('assessedAt', 'assessment date', FIELD_PATTERNS.assessedAt, 0.05),
      group('expiresAt', 'expiry date', FIELD_PATTERNS.expiresAt, 0.05),
    ],
    folderBias: { orgchart: 0.07 },
    buildProfile: buildSkillAssertionProfile,
  },
  {
    intent: 'credential_assertion',
    label: 'Credential assertions',
    targetFolderKind: 'orgchart',
    targetProfileId: 'orgchart',
    evidenceMode: 'row_snapshot',
    groups: [
      group('actor', 'person or position', [...FIELD_PATTERNS.person, ...FIELD_PATTERNS.email, ...FIELD_PATTERNS.positionId], 0.22, true),
      group('credential', 'credential', [...FIELD_PATTERNS.credentialId, ...FIELD_PATTERNS.credential], 0.26, true),
      group('issuer', 'issuer', FIELD_PATTERNS.issuer, 0.12),
      group('issuedAt', 'issue date', FIELD_PATTERNS.issuedAt, 0.08),
      group('expiresAt', 'expiry date', FIELD_PATTERNS.expiresAt, 0.1),
      group('verificationStatus', 'verification status', FIELD_PATTERNS.verificationStatus, 0.1),
      group('evidence', 'evidence', FIELD_PATTERNS.evidence, 0.05),
    ],
    folderBias: { orgchart: 0.07 },
    buildProfile: buildCredentialAssertionProfile,
  },
  {
    intent: 'skill_requirement',
    label: 'Skill or credential requirements',
    targetFolderKind: 'orgchart',
    targetProfileId: 'orgchart',
    evidenceMode: 'row_snapshot',
    groups: [
      group('target', 'role, position, team, or project', [...FIELD_PATTERNS.target, ...FIELD_PATTERNS.project, ...FIELD_PATTERNS.positionId], 0.24, true),
      group('skill', 'required skill', ['required skill', 'skill id', 'skill name', 'required competency'], 0.24),
      group('credential', 'required credential', ['required credential', 'credential id', 'required certification'], 0.2),
      group('proficiency', 'required proficiency', ['required proficiency', 'minimum proficiency', 'minimum level'], 0.14),
      group('proficiencyScale', 'source scale', FIELD_PATTERNS.proficiencyScale, 0.08),
      group('required', 'required flag', FIELD_PATTERNS.required, 0.06),
    ],
    folderBias: { orgchart: 0.07, process: 0.03, grc: 0.03 },
    buildProfile: buildSkillRequirementProfile,
  },
  {
    intent: 'succession_planning',
    label: 'Succession nominations',
    targetFolderKind: 'orgchart',
    targetProfileId: 'orgchart',
    // Whole-row evidence can contain successor identity/readiness columns and
    // must not be copied onto the raw Person/Position records. The reviewed
    // protected relationship retains the source identity and mapped values.
    evidenceMode: 'source_identity',
    groups: [
      exactGroup('positionId', 'target Position ID', FIELD_PATTERNS.positionId, 0.28, true),
      group('personId', 'current employee ID', [...FIELD_PATTERNS.personId, ...FIELD_PATTERNS.email], 0.1),
      group('person', 'current employee name', FIELD_PATTERNS.person, 0.08),
      exactGroup('successorId', 'successor employee ID', FIELD_PATTERNS.successorId, 0.27),
      exactGroup('successor', 'successor name', FIELD_PATTERNS.successor, 0.27),
      exactGroup('readiness', 'successor readiness', FIELD_PATTERNS.successionReadiness, 0.08),
      exactGroup('rank', 'nomination rank', FIELD_PATTERNS.successionRank, 0.05),
    ],
    folderBias: { orgchart: 0.1 },
    buildProfile: buildSuccessionPlanningProfile,
  },
  {
    intent: 'talent_observation',
    label: 'Talent observations',
    targetFolderKind: 'orgchart',
    targetProfileId: 'orgchart',
    evidenceMode: 'row_snapshot',
    groups: [
      group('actor', 'person', [...FIELD_PATTERNS.personId, ...FIELD_PATTERNS.email, ...FIELD_PATTERNS.person], 0.2, true),
      group('dimension', 'talent dimension', FIELD_PATTERNS.talentDimension, 0.2, true),
      group('observedValue', 'observed value', FIELD_PATTERNS.observedValue, 0.17, true),
      group('proficiencyScale', 'scale ID', FIELD_PATTERNS.proficiencyScale, 0.1, true),
      group('scaleVersion', 'scale version', FIELD_PATTERNS.scaleVersion, 0.08, true),
      group('cycleId', 'review cycle', FIELD_PATTERNS.cycleId, 0.12, true),
      group('assessedAt', 'observed at', FIELD_PATTERNS.assessedAt, 0.04),
      group('expiresAt', 'expires at', FIELD_PATTERNS.expiresAt, 0.03),
      group('sourceObservationId', 'source observation ID', FIELD_PATTERNS.sourceObservationId, 0.06),
    ],
    folderBias: { orgchart: 0.08 },
    buildProfile: buildTalentObservationProfile,
  },
  {
    intent: 'task_tracker',
    label: 'Tasks or tickets',
    targetFolderKind: 'strategy',
    targetProfileId: 'gantt',
    evidenceMode: 'row_snapshot',
    groups: [
      group('issue', 'issue or ticket key', FIELD_PATTERNS.issue, 0.22, true),
      group('summary', 'summary or title', FIELD_PATTERNS.summary, 0.2, true),
      group('status', 'status', FIELD_PATTERNS.status, 0.18, true),
      group('assignee', 'assignee', ASSIGNEE_PATTERNS, 0.16),
      group('sprint', 'sprint or milestone', FIELD_PATTERNS.sprint, 0.08),
      group('epic', 'epic or parent', FIELD_PATTERNS.epic, 0.08),
    ],
    folderBias: { strategy: 0.08, process: 0.03 },
    buildProfile: buildTaskTrackerProfile,
  },
  {
    intent: 'process_raci',
    label: 'Process ownership or RACI',
    targetFolderKind: 'process',
    targetProfileId: 'process',
    evidenceMode: 'row_snapshot',
    groups: [
      group('process', 'process', FIELD_PATTERNS.process, 0.18, true),
      group('activity', 'activity or step', FIELD_PATTERNS.activity, 0.18, true),
      group('actor', 'role or person', [...FIELD_PATTERNS.person, ...ASSIGNEE_PATTERNS], 0.16, true),
      group('raci', 'RACI or responsibility', FIELD_PATTERNS.raci, 0.2, true),
      group('system', 'system or application', FIELD_PATTERNS.application, 0.06),
    ],
    folderBias: { process: 0.1 },
    buildProfile: buildProcessRaciProfile,
  },
  {
    intent: 'people_directory',
    label: 'People directory',
    targetFolderKind: 'orgchart',
    targetProfileId: 'orgchart',
    evidenceMode: 'row_snapshot',
    groups: [
      group('person', 'person name', FIELD_PATTERNS.person, 0.2, true),
      group('email', 'email', FIELD_PATTERNS.email, 0.18, true),
      group('title', 'title or position', FIELD_PATTERNS.title, 0.14),
      group('department', 'department or team', FIELD_PATTERNS.department, 0.14),
    ],
    folderBias: { orgchart: 0.1 },
    buildProfile: buildPeopleProfile,
  },
  {
    intent: 'position_hierarchy',
    label: 'Position hierarchy',
    targetFolderKind: 'orgchart',
    targetProfileId: 'orgchart',
    evidenceMode: 'row_snapshot',
    groups: [
      exactGroup('positionId', 'position ID or code', FIELD_PATTERNS.positionId, 0.28, true),
      exactGroup('managerPositionId', 'manager position ID', FIELD_PATTERNS.managerPositionId, 0.22),
      // Many seat-per-row exports name the manager as a person (an employee
      // id or name) rather than a position. That still defines the hierarchy:
      // the manager's own row says which seat they hold.
      group('manager', 'manager (person)', FIELD_PATTERNS.manager, 0.1),
      group('managerId', 'manager ID or email', FIELD_PATTERNS.managerId, 0.1),
      group('title', 'position title', FIELD_PATTERNS.title, 0.14, true),
      group('person', 'incumbent', [...FIELD_PATTERNS.person, ...FIELD_PATTERNS.email], 0.12),
      group('personId', 'incumbent ID', [...FIELD_PATTERNS.personId, ...FIELD_PATTERNS.email], 0.12),
      group('department', 'department', FIELD_PATTERNS.department, 0.08),
    ],
    folderBias: { orgchart: 0.16 },
    buildProfile: buildPositionHierarchyProfile,
  },
  {
    intent: 'person_hierarchy',
    label: 'Person hierarchy with derived positions',
    targetFolderKind: 'orgchart',
    targetProfileId: 'orgchart',
    evidenceMode: 'row_snapshot',
    groups: [
      group('person', 'person', FIELD_PATTERNS.person, 0.16, true),
      group('personId', 'person ID or email', [...FIELD_PATTERNS.personId, ...FIELD_PATTERNS.email], 0.2, true),
      group('manager', 'manager', FIELD_PATTERNS.manager, 0.16, true),
      group('managerId', 'manager ID or email', FIELD_PATTERNS.managerId, 0.18),
      group('secondaryManager', 'secondary manager', FIELD_PATTERNS.secondaryManager, 0.1),
      group('secondaryManagerId', 'secondary manager ID or email', FIELD_PATTERNS.secondaryManagerId, 0.1),
      exactGroup('positionId', 'position ID or code', FIELD_PATTERNS.positionId, 0.16),
      exactGroup('managerPositionId', 'manager position ID', FIELD_PATTERNS.managerPositionId, 0.12),
      group('title', 'title', FIELD_PATTERNS.title, 0.12, true),
      group('department', 'department', FIELD_PATTERNS.department, 0.08),
      group('departureDate', 'leaving date', FIELD_PATTERNS.departureDate, 0.04),
    ],
    folderBias: { orgchart: 0.14 },
    buildProfile: buildPersonHierarchyProfile,
  },
  {
    intent: 'org_structure',
    label: 'Org structure (legacy person hierarchy)',
    targetFolderKind: 'orgchart',
    targetProfileId: 'orgchart',
    evidenceMode: 'row_snapshot',
    groups: [
      group('person', 'person', [...FIELD_PATTERNS.person, ...FIELD_PATTERNS.email], 0.2, true),
      group('manager', 'manager or reports-to', FIELD_PATTERNS.manager, 0.24, true),
      group('secondaryManager', 'secondary manager', FIELD_PATTERNS.secondaryManager, 0.1),
      group('secondaryManagerId', 'secondary manager ID or email', FIELD_PATTERNS.secondaryManagerId, 0.1),
      group('title', 'title', FIELD_PATTERNS.title, 0.1),
      group('department', 'department', FIELD_PATTERNS.department, 0.1),
      group('departureDate', 'leaving date', FIELD_PATTERNS.departureDate, 0.04),
    ],
    folderBias: { orgchart: 0.12 },
    buildProfile: buildPersonHierarchyProfile,
  },
  {
    intent: 'control_risk_register',
    label: 'Controls and risks',
    targetFolderKind: 'grc',
    targetProfileId: 'grc-control',
    evidenceMode: 'row_snapshot',
    groups: [
      group('control', 'control', FIELD_PATTERNS.control, 0.2, true),
      group('risk', 'risk', FIELD_PATTERNS.risk, 0.16),
      group('framework', 'framework or obligation', FIELD_PATTERNS.framework, 0.14),
      group('evidence', 'evidence', FIELD_PATTERNS.evidence, 0.1),
      group('finding', 'finding', FIELD_PATTERNS.finding, 0.1),
    ],
    folderBias: { grc: 0.1, process: 0.04 },
    buildProfile: buildControlRiskProfile,
  },
  {
    intent: 'access_grants',
    label: 'Access grants',
    targetFolderKind: 'grc',
    targetProfileId: 'grc-control',
    evidenceMode: 'row_snapshot',
    groups: [
      group('actor', 'user or email', [...FIELD_PATTERNS.person, ...FIELD_PATTERNS.email], 0.2, true),
      group('application', 'application or system', FIELD_PATTERNS.application, 0.2, true),
      group('permission', 'permission or entitlement', FIELD_PATTERNS.permission, 0.2, true),
      group('group', 'group or role', FIELD_PATTERNS.group, 0.1),
    ],
    buildProfile: buildAccessGrantProfile,
  },
  {
    intent: 'system_inventory',
    label: 'System inventory',
    targetFolderKind: 'network',
    targetProfileId: 'network',
    evidenceMode: 'source_identity',
    groups: [
      group('resource', 'resource id or ARN', FIELD_PATTERNS.resource, 0.24, true),
      group('service', 'service', FIELD_PATTERNS.service, 0.15),
      group('application', 'system or application', FIELD_PATTERNS.application, 0.14),
    ],
    folderBias: { network: 0.12 },
    buildProfile: buildSystemInventoryProfile,
  },
  {
    intent: 'software_design_graph',
    label: 'Software design graph',
    targetFolderKind: 'software_design',
    targetProfileId: 'component',
    evidenceMode: 'source_identity',
    groups: [
      group('component', 'component or API', FIELD_PATTERNS.component, 0.24, true),
      group('database', 'database/table/schema', FIELD_PATTERNS.database, 0.16),
      group('service', 'service', FIELD_PATTERNS.service, 0.12),
    ],
    folderBias: { software_design: 0.12 },
    buildProfile: buildSoftwareDesignProfile,
  },
  {
    intent: 'process_inventory',
    label: 'Process inventory',
    targetFolderKind: 'process',
    targetProfileId: 'process',
    evidenceMode: 'row_snapshot',
    groups: [
      group('processId', 'process id', ['process id', 'process_id', 'processid'], 0.16),
      group('name', 'process name', ['name', 'process name', 'title', ...FIELD_PATTERNS.process], 0.18, true),
      group('activity', 'activity or step', FIELD_PATTERNS.activity, 0.1),
      group('actor', 'owner or role', ASSIGNEE_PATTERNS, 0.1),
      group('department', 'department', FIELD_PATTERNS.department, 0.07),
      group('frequency', 'frequency', ['frequency', 'cadence', 'schedule'], 0.06),
      group('duration', 'average duration', ['average duration', 'avg duration', 'duration days'], 0.07),
      group('criticality', 'criticality', ['criticality', 'business criticality', 'priority'], 0.06),
      group('status', 'status', FIELD_PATTERNS.status, 0.06),
      group('description', 'description', ['description', 'summary', 'details'], 0.05),
      group('controls', 'related controls', ['related controls', 'control ids', 'controls'], 0.07),
      group('system', 'system', FIELD_PATTERNS.application, 0.05),
    ],
    folderBias: { process: 0.12 },
    buildProfile: buildProcessInventoryProfile,
  },
];

const AUTO_CONFIRM_MIN_CONFIDENCE = 0.78;
const AUTO_CONFIRM_MIN_MARGIN = 0.08;

export class SpreadsheetIntentResolver {
  resolve(input: SpreadsheetIntentResolverInput): SpreadsheetIntentResolution {
    const headers = normalizeHeaders(input.headers);
    const sourceSystem = input.sourceSystem?.trim() || 'spreadsheet';
    const candidates = INTENT_SPECS
      .map((spec) => scoreIntent(spec, headers, input, sourceSystem))
      .filter((candidate): candidate is SpreadsheetIntentCandidate =>
        candidate !== null,
      )
      .sort((a, b) => b.confidence - a.confidence)
      .slice(0, input.maxCandidates ?? 3);

    const out = candidates.length > 0
      ? candidates
      : [buildGenericCandidate(input, sourceSystem)];
    const recommended = out[0];
    const runnerUp = out[1] ?? null;
    const hasNearTie =
      !!runnerUp &&
      runnerUp.confidence >= AUTO_CONFIRM_MIN_CONFIDENCE &&
      recommended.confidence - runnerUp.confidence < AUTO_CONFIRM_MIN_MARGIN;
    return {
      sourceSystem,
      candidates: out,
      recommended,
      hasNearTie,
      requiresUserConfirmation:
        recommended.mappingProfile.requiresReview ||
        recommended.intent === 'generic_table' ||
        recommended.confidence < AUTO_CONFIRM_MIN_CONFIDENCE ||
        // A structurally impossible mapping must reach a human no matter how
        // confidently it scored. The manager self-loop shipped at 0.98.
        recommended.invariantViolations.length > 0 ||
        hasNearTie,
    };
  }

  /**
   * Rebuild the canonical mapping contract from the user's reviewed column
   * selections. Rebuilding through the intent spec keeps repeated entity,
   * identity, and relationship references consistent.
   */
  buildReviewedMappingProfile(input: {
    readonly candidate: SpreadsheetIntentCandidate;
    readonly headers: readonly string[];
    readonly selections: SpreadsheetMappingReviewSelection;
  }): SourceMappingProfile {
    const spec = INTENT_SPECS.find(
      (candidateSpec) => candidateSpec.intent === input.candidate.intent,
    );
    if (!spec) {
      return SourceMappingProfileSchema.parse({
        ...input.candidate.mappingProfile,
        version: (input.candidate.mappingProfile.version ?? 0) + 1,
        requiresReview: false,
      });
    }

    const headers = normalizeHeaders(input.headers);
    const availableHeaders = new Set(headers.map((header) => header.original));
    const matches = spec.groups.map((mappingGroup) => {
      const selectedField = input.selections[mappingGroup.id] ?? null;
      if (selectedField && !availableHeaders.has(selectedField)) {
        throw new Error(
          `Mapped column "${selectedField}" is not present in this source.`,
        );
      }
      if (mappingGroup.recommended && !selectedField) {
        throw new Error(`${mappingGroup.label} must be mapped before import.`);
      }
      return { group: mappingGroup, field: selectedField };
    });
    if (spec.intent === 'succession_planning'
      && !matches.some((match) =>
        (match.group.id === 'successorId' || match.group.id === 'successor')
        && Boolean(match.field)
      )) {
      throw new Error('Successor employee ID or successor name must be mapped before import.');
    }
    if (spec.intent === 'position_hierarchy' && !hasReportingMapping(matches)) {
      throw new Error(POSITION_HIERARCHY_REPORTING_REQUIRED);
    }
    if (spec.intent === 'position_hierarchy' && personKeyedBySeat(matches)) {
      throw new Error(POSITION_HIERARCHY_PERSON_KEYED_BY_SEAT);
    }

    const mappingProfile = spec.buildProfile({
      spec,
      sourceSystem: input.candidate.mappingProfile.sourceSystem,
      confidence: input.candidate.confidence,
      headers,
      matches,
    });

    const reviewedRegisteredFields = input.candidate.availableRegisteredFields
      .flatMap((targetField): SpreadsheetPreservedField[] => {
        const sourceField = input.selections[registeredFieldSelectionId(targetField)];
        if (!sourceField) return [];
        if (!availableHeaders.has(sourceField)) {
          throw new Error(
            `Mapped column "${sourceField}" is not present in this source.`,
          );
        }
        return [{
          sourceField,
          targetField,
          entityRef: input.candidate.registeredFieldEntityRefs[targetField]
            ?? DEFAULT_PRESERVED_FIELD_ENTITY,
        }];
      });
    const profileWithPreservedFields = appendPreservedFields(
      mappingProfile,
      reviewedRegisteredFields,
    );

    return SourceMappingProfileSchema.parse({
      ...profileWithPreservedFields,
      version: (input.candidate.mappingProfile.version ?? 0) + 1,
      requiresReview: false,
    });
  }
}

function scoreIntent(
  spec: IntentSpec,
  headers: readonly NormalizedHeader[],
  input: SpreadsheetIntentResolverInput,
  sourceSystem: string,
): SpreadsheetIntentCandidate | null {
  const matches = matchGroupsToHeaders(spec.groups, headers);
  const matchedWeight = matches.reduce(
    (sum, match) => sum + (match.field ? match.group.weight : 0),
    0,
  );
  const folderBonus = input.folderKind && spec.folderBias?.[input.folderKind]
    ? spec.folderBias[input.folderKind]
    : 0;
  const confidence = clamp(matchedWeight + folderBonus, 0, 0.98);
  if (confidence < 0.22) return null;

  const reasons = matches
    .filter((match) => match.field)
    .map((match) => `${match.group.label}: ${match.field}`);
  const missingRecommendedFields = matches
    .filter((match) => match.group.recommended && !match.field)
    .map((match) => match.group.label);
  const mappingProfile = spec.buildProfile({
    spec,
    sourceSystem,
    confidence,
    headers,
    matches,
  });
  const availableRegisteredFields = supportsRegisteredFieldPreservation(spec.intent)
    ? Object.keys(input.importableFieldAliases ?? {})
        .filter((fieldId) => !(isHierarchyIntent(spec.intent) && STRUCTURAL_REFERENCE_FIELDS.has(fieldId)))
        .sort()
    : [];
  const registeredFieldEntityRefs = routeRegisteredFieldEntities(
    spec.intent,
    mappingProfile,
    availableRegisteredFields,
    input.importableFieldEntityRefs,
  );
  const registeredFields = supportsRegisteredFieldPreservation(spec.intent)
    ? resolvePreservedFields(headers, input.importableFieldAliases)
        .filter((field) => availableRegisteredFields.includes(field.targetField))
        .map((field) => ({
          ...field,
          entityRef: registeredFieldEntityRefs[field.targetField] ?? DEFAULT_PRESERVED_FIELD_ENTITY,
        }))
    : [];
  const preservedFields = preservedFieldsForProfile(
    mappingProfile,
    registeredFields,
  );
  const mappingProfileWithPreservedFields = appendPreservedFields(
    mappingProfile,
    registeredFields,
  );
  const recognizedSourceFields = new Set(
    mappingProfileWithPreservedFields.entityMappings.flatMap((mapping) =>
      mapping.fieldMappings.map((fieldMapping) => fieldMapping.sourceField),
    ),
  );
  return {
    intent: spec.intent,
    label: spec.label,
    confidence,
    targetFolderKind: spec.targetFolderKind,
    targetProfileId: spec.targetProfileId,
    reasons,
    missingRecommendedFields,
    reviewFields: matches.map((match) => ({
      id: match.group.id,
      label: match.group.label,
      sourceField: match.field,
      required: match.group.recommended ?? false,
    })),
    registeredFields,
    availableRegisteredFields,
    preservedFields,
    registeredFieldEntityRefs,
    unrecognizedHeaders: headers
      .map((header) => header.original)
      .filter((header) => !recognizedSourceFields.has(header)),
    invariantViolations: mappingProfileInvariantViolations(
      mappingProfileWithPreservedFields,
    ),
    mappingProfile: mappingProfileWithPreservedFields,
  };
}

function buildProjectAllocationProfile(input: BuildProfileInput): SourceMappingProfile {
  const actorField = field(input.matches, 'actor');
  const actorFields = actorIdentityFields(input, actorField);
  const projectField = field(input.matches, 'project');
  const allocationField = field(input.matches, 'allocation');
  const effortField = field(input.matches, 'effort');
  const startField = field(input.matches, 'start');
  const endField = field(input.matches, 'end');
  const basisField = field(input.matches, 'basis');
  return profile(input, {
    entityMappings: [
      entity('actor', 'actor', 'orgchart', compact([
        maybeMap(actorFields.labelField ?? actorFields.identityField, 'props.name', 'label', true),
        maybeMap(actorFields.emailField, 'props.email', 'external_ref'),
      ])),
      entity('target', 'project', 'orgchart', compact([
        maybeMap(projectField, 'props.name', 'label', true),
      ])),
    ],
    relationMappings: [{
      id: 'capacity-commitment',
      predicate: 'project_assignment',
      sourceEntityRef: 'actor',
      targetEntityRef: 'target',
      qualifierMappings: compact([
        maybeMap(allocationField, 'qualifiers.allocation', 'allocation'),
        maybeMap(effortField, 'qualifiers.effort', 'effort'),
        maybeMap(startField, 'qualifiers.validFrom', 'date_start'),
        maybeMap(endField, 'qualifiers.validTo', 'date_end'),
        maybeMap(basisField, 'qualifiers.basis', 'provenance'),
      ]),
      defaultQualifiers: { basis: 'source_reported' },
    }],
    identityRules: compact([
      actorFields.identityField
        ? {
            id: 'actor-identity',
            entityRef: 'actor',
            strategy: actorFields.identityStrategy,
            fields: [actorFields.identityField],
            targetProfileId: 'orgchart',
          } as const
        : null,
      actorFields.identityField && actorFields.labelField &&
        actorFields.identityField !== actorFields.labelField
        ? {
            id: 'actor-label',
            entityRef: 'actor',
            strategy: 'label_match',
            fields: [actorFields.labelField],
            targetProfileId: 'orgchart',
            confidence: 0.65,
          } as const
        : null,
      projectField
        ? {
            id: 'target-identity',
            entityRef: 'target',
            strategy: 'label_match',
            fields: [projectField],
          } as const
        : null,
    ]),
  });
}

function buildCapacityCommitmentProfile(input: BuildProfileInput): SourceMappingProfile {
  const actorField = field(input.matches, 'actor');
  const actorFields = actorIdentityFields(input, actorField);
  const targetField = field(input.matches, 'target');
  const targetKindField = field(input.matches, 'targetKind');
  const allocationField = field(input.matches, 'allocation');
  const effortField = field(input.matches, 'effort');
  const effortUnitField = field(input.matches, 'effortUnit');
  const roleField = field(input.matches, 'role');
  const startField = field(input.matches, 'start');
  const endField = field(input.matches, 'end');
  const basisField = field(input.matches, 'basis');
  const commitmentModeField = field(input.matches, 'commitmentMode');
  return profile(input, {
    entityMappings: [
      entity('actor', 'actor', 'orgchart', compact([
        maybeMap(actorFields.labelField ?? actorFields.identityField, 'props.name', 'label', true),
        maybeMap(actorFields.emailField, 'props.email', 'external_ref'),
      ])),
      entity('target', 'work_context', 'orgchart', compact([
        maybeMap(targetField, 'props.name', 'label', true),
        maybeMap(targetField, 'props.externalRef.key', 'external_ref'),
        maybeMap(targetKindField, 'props.targetKind', 'relationship_target', true),
      ])),
    ],
    relationMappings: [{
      id: 'capacity-commitment',
      predicate: 'capacity_commitment',
      sourceEntityRef: 'actor',
      targetEntityRef: 'target',
      qualifierMappings: compact([
        maybeMap(allocationField, 'qualifiers.allocation', 'allocation'),
        maybeMap(effortField, 'qualifiers.effort', 'effort'),
        maybeMap(effortUnitField, 'qualifiers.effortUnit', 'effort'),
        maybeMap(roleField, 'qualifiers.role', 'predicate'),
        maybeMap(targetKindField, 'qualifiers.targetKind', 'relationship_target'),
        maybeMap(startField, 'qualifiers.validFrom', 'date_start'),
        maybeMap(endField, 'qualifiers.validTo', 'date_end'),
        maybeMap(basisField, 'qualifiers.basis', 'provenance'),
        maybeMap(commitmentModeField, 'qualifiers.commitmentMode', 'status'),
      ]),
      defaultQualifiers: {
        basis: 'source_capacity_commitment',
        facet: 'capacity_commitment',
      },
    }],
    identityRules: compact([
      actorFields.identityField
        ? {
            id: 'actor-identity',
            entityRef: 'actor',
            strategy: actorFields.identityStrategy,
            fields: [actorFields.identityField],
            targetProfileId: 'orgchart',
          } as const
        : null,
      actorFields.identityField && actorFields.labelField &&
        actorFields.identityField !== actorFields.labelField
        ? {
            id: 'actor-label',
            entityRef: 'actor',
            strategy: 'label_match',
            fields: [actorFields.labelField],
            targetProfileId: 'orgchart',
            confidence: 0.65,
          } as const
        : null,
      targetField
        ? {
            id: 'target-identity',
            entityRef: 'target',
            strategy: 'external_ref',
            fields: [targetField],
          } as const
        : null,
    ]),
  });
}

function buildTeamMembershipProfile(input: BuildProfileInput): SourceMappingProfile {
  const actorField = field(input.matches, 'actor');
  const actorFields = actorIdentityFields(input, actorField);
  const teamField = field(input.matches, 'team');
  const roleField = field(input.matches, 'role');
  const typeField = field(input.matches, 'membershipType');
  const startField = field(input.matches, 'start');
  const endField = field(input.matches, 'end');
  return profile(input, {
    entityMappings: [
      entity('actor', 'actor', 'orgchart', compact([
        maybeMap(actorFields.labelField ?? actorFields.identityField, 'props.name', 'label', true),
        maybeMap(actorFields.emailField, 'props.email', 'external_ref'),
      ])),
      entity('group', 'team', 'orgchart', compact([maybeMap(teamField, 'props.name', 'label', true)])),
    ],
    relationMappings: [{
      id: 'team-membership',
      predicate: 'member_of',
      sourceEntityRef: 'actor',
      targetEntityRef: 'group',
      qualifierMappings: compact([
        maybeMap(roleField, 'qualifiers.role', 'predicate'),
        maybeMap(typeField, 'qualifiers.membershipType', 'status'),
        maybeMap(startField, 'qualifiers.validFrom', 'date_start'),
        maybeMap(endField, 'qualifiers.validTo', 'date_end'),
      ]),
      defaultQualifiers: { groupKind: 'team', membershipType: 'member' },
    }],
    identityRules: compact([
      actorFields.identityField ? { id: 'actor-identity', entityRef: 'actor', strategy: actorFields.identityStrategy, fields: [actorFields.identityField], targetProfileId: 'orgchart' } as const : null,
      teamField ? { id: 'group-identity', entityRef: 'group', strategy: 'label_match', fields: [teamField], targetProfileId: 'orgchart' } as const : null,
    ]),
  });
}

function buildWorkforceDemandProfile(input: BuildProfileInput): SourceMappingProfile {
  const targetField = field(input.matches, 'target');
  const fteField = field(input.matches, 'requiredFte');
  const roleField = field(input.matches, 'role');
  const startField = field(input.matches, 'start');
  const endField = field(input.matches, 'end');
  const basisField = field(input.matches, 'basis');
  return profile(input, {
    entityMappings: [
      entity('target', 'work_context', 'orgchart', compact([maybeMap(targetField, 'props.name', 'label', true)])),
      entity('role', 'role_profile', 'orgchart', compact([maybeMap(roleField, 'props.name', 'label', true)])),
    ],
    relationMappings: [{
      id: 'workforce-demand',
      predicate: 'requires_capacity',
      sourceEntityRef: 'target',
      targetEntityRef: 'role',
      qualifierMappings: compact([
        maybeMap(fteField, 'qualifiers.requiredFte', 'allocation'),
        maybeMap(roleField, 'qualifiers.role', 'predicate'),
        maybeMap(startField, 'qualifiers.validFrom', 'date_start'),
        maybeMap(endField, 'qualifiers.validTo', 'date_end'),
        maybeMap(basisField, 'qualifiers.basis', 'provenance'),
      ]),
      defaultQualifiers: { basis: 'source_workforce_demand', facet: 'capacity_commitment' },
    }],
    identityRules: compact([
      targetField ? { id: 'target-identity', entityRef: 'target', strategy: 'label_match', fields: [targetField], targetProfileId: 'orgchart' } as const : null,
      roleField ? { id: 'role-identity', entityRef: 'role', strategy: 'label_match', fields: [roleField], targetProfileId: 'orgchart' } as const : null,
    ]),
  });
}

function buildSkillCatalogProfile(input: BuildProfileInput): SourceMappingProfile {
  const skillId = field(input.matches, 'skillId');
  const skillLabel = field(input.matches, 'skill');
  const taxonomy = field(input.matches, 'taxonomy');
  const taxonomyVersion = field(input.matches, 'taxonomyVersion');
  return profile(input, {
    entityMappings: [entity('skill', 'skill', 'orgchart', compact([
      maybeMap(skillId, 'props.skillConceptId', 'external_ref', true),
      maybeMap(skillLabel, 'props.skillLabel', 'label', true),
      maybeMap(skillLabel, 'props.name', 'label'),
      maybeMap(taxonomy, 'props.taxonomyId', 'provenance'),
      maybeMap(taxonomyVersion, 'props.taxonomyVersion', 'provenance'),
    ]))],
    relationMappings: [],
    identityRules: skillId ? [{ id: 'skill-identity', entityRef: 'skill', strategy: 'external_ref', fields: [skillId], targetProfileId: 'orgchart' }] : [],
  });
}

function buildSkillAssertionProfile(input: BuildProfileInput): SourceMappingProfile {
  const actorField = field(input.matches, 'actor');
  const actorFields = actorIdentityFields(input, actorField);
  const skillField = field(input.matches, 'skill');
  const proficiency = field(input.matches, 'proficiency');
  const scale = field(input.matches, 'proficiencyScale');
  const evidence = field(input.matches, 'evidence');
  const assessedAt = field(input.matches, 'assessedAt');
  const expiresAt = field(input.matches, 'expiresAt');
  return profile(input, {
    entityMappings: [
      entity('actor', 'actor', 'orgchart', compact([
        maybeMap(actorFields.labelField ?? actorFields.identityField, 'props.name', 'label', true),
        maybeMap(actorFields.emailField, 'props.email', 'external_ref'),
      ])),
      entity('skill', 'skill', 'orgchart', compact([
        maybeMap(skillField, 'props.skillConceptId', 'external_ref', true),
        maybeMap(skillField, 'props.skillLabel', 'label'),
        maybeMap(skillField, 'props.name', 'label'),
      ])),
    ],
    relationMappings: [{
      id: 'skill-assertion', predicate: 'has_skill', sourceEntityRef: 'actor', targetEntityRef: 'skill',
      qualifierMappings: compact([
        maybeMap(proficiency, 'qualifiers.sourceProficiency', 'payload'),
        maybeMap(scale, 'qualifiers.sourceScale', 'provenance'),
        maybeMap(evidence, 'qualifiers.evidenceRef', 'provenance'),
        maybeMap(assessedAt, 'qualifiers.assessedAt', 'date_start'),
        maybeMap(expiresAt, 'qualifiers.expiresAt', 'date_end'),
      ]),
      defaultQualifiers: { scaleMappingStatus: 'unreviewed' },
    }],
    identityRules: compact([
      actorFields.identityField ? { id: 'actor-identity', entityRef: 'actor', strategy: actorFields.identityStrategy, fields: [actorFields.identityField], targetProfileId: 'orgchart' } as const : null,
      skillField ? { id: 'skill-identity', entityRef: 'skill', strategy: 'external_ref', fields: [skillField], targetProfileId: 'orgchart' } as const : null,
    ]),
  });
}

function buildCredentialAssertionProfile(input: BuildProfileInput): SourceMappingProfile {
  const actorField = field(input.matches, 'actor');
  const actorFields = actorIdentityFields(input, actorField);
  const credential = field(input.matches, 'credential');
  const issuer = field(input.matches, 'issuer');
  const issuedAt = field(input.matches, 'issuedAt');
  const expiresAt = field(input.matches, 'expiresAt');
  const verificationStatus = field(input.matches, 'verificationStatus');
  const evidence = field(input.matches, 'evidence');
  return profile(input, {
    entityMappings: [
      entity('actor', 'actor', 'orgchart', compact([
        maybeMap(actorFields.labelField ?? actorFields.identityField, 'props.name', 'label', true),
        maybeMap(actorFields.emailField, 'props.email', 'external_ref'),
      ])),
      entity('credential', 'credential', 'orgchart', compact([
        maybeMap(credential, 'props.credentialConceptId', 'external_ref', true),
        maybeMap(credential, 'props.credentialLabel', 'label'),
        maybeMap(credential, 'props.name', 'label'),
      ])),
    ],
    relationMappings: [{
      id: 'credential-assertion', predicate: 'holds_credential', sourceEntityRef: 'actor', targetEntityRef: 'credential',
      qualifierMappings: compact([
        maybeMap(issuer, 'qualifiers.issuer', 'provenance'),
        maybeMap(issuedAt, 'qualifiers.issuedAt', 'date_start'),
        maybeMap(expiresAt, 'qualifiers.expiresAt', 'date_end'),
        maybeMap(verificationStatus, 'qualifiers.verificationStatus', 'status'),
        maybeMap(evidence, 'qualifiers.evidenceRef', 'provenance'),
      ]),
      defaultQualifiers: {},
    }],
    identityRules: compact([
      actorFields.identityField ? { id: 'actor-identity', entityRef: 'actor', strategy: actorFields.identityStrategy, fields: [actorFields.identityField], targetProfileId: 'orgchart' } as const : null,
      credential ? { id: 'credential-identity', entityRef: 'credential', strategy: 'external_ref', fields: [credential], targetProfileId: 'orgchart' } as const : null,
    ]),
  });
}

function buildSkillRequirementProfile(input: BuildProfileInput): SourceMappingProfile {
  const target = field(input.matches, 'target');
  const skill = field(input.matches, 'skill');
  const credential = field(input.matches, 'credential');
  const proficiency = field(input.matches, 'proficiency');
  const scale = field(input.matches, 'proficiencyScale');
  const required = field(input.matches, 'required');
  const capabilityField = skill ?? credential;
  const usesCredential = Boolean(credential && !skill);
  const capabilityRef = usesCredential ? 'credential' : 'skill';
  const predicate = usesCredential ? 'requires_credential' : 'requires_skill';
  return profile(input, {
    entityMappings: [
      entity('target', 'work_context', 'orgchart', compact([maybeMap(target, 'props.name', 'label', true)])),
      entity(capabilityRef, capabilityRef, 'orgchart', compact([
        maybeMap(capabilityField, `props.${usesCredential ? 'credentialConceptId' : 'skillConceptId'}`, 'external_ref', true),
        maybeMap(capabilityField, `props.${usesCredential ? 'credentialLabel' : 'skillLabel'}`, 'label'),
        maybeMap(capabilityField, 'props.name', 'label'),
      ])),
    ],
    relationMappings: [{
      id: 'capability-requirement', predicate, sourceEntityRef: 'target', targetEntityRef: capabilityRef,
      qualifierMappings: compact([
        maybeMap(proficiency, 'qualifiers.sourceProficiency', 'payload'),
        maybeMap(scale, 'qualifiers.sourceScale', 'provenance'),
        maybeMap(required, 'qualifiers.required', 'status'),
      ]),
      defaultQualifiers: { required: true, scaleMappingStatus: 'unreviewed' },
    }],
    identityRules: compact([
      target ? { id: 'target-identity', entityRef: 'target', strategy: 'external_ref', fields: [target], targetProfileId: 'orgchart' } as const : null,
      capabilityField ? { id: 'capability-identity', entityRef: capabilityRef, strategy: 'external_ref', fields: [capabilityField], targetProfileId: 'orgchart' } as const : null,
    ]),
  });
}

function buildTalentObservationProfile(input: BuildProfileInput): SourceMappingProfile {
  const actorField = field(input.matches, 'actor');
  const actorFields = actorIdentityFields(input, actorField);
  const observationId = field(input.matches, 'sourceObservationId');
  const dimension = field(input.matches, 'dimension');
  const observedValue = field(input.matches, 'observedValue');
  const scaleId = field(input.matches, 'proficiencyScale');
  const scaleVersion = field(input.matches, 'scaleVersion');
  const cycleId = field(input.matches, 'cycleId');
  const assessedAt = field(input.matches, 'assessedAt');
  const expiresAt = field(input.matches, 'expiresAt');
  return profile(input, {
    entityMappings: [
      entity('subject', 'actor', 'orgchart', compact([
        maybeMap(actorFields.labelField ?? actorFields.identityField, 'props.name', 'label', true),
        maybeMap(actorFields.emailField, 'props.email', 'external_ref'),
      ])),
      entity('observation', 'talent_observation', 'orgchart', compact([
        maybeMap(observationId, 'props.sourceObservationId', 'external_ref', true),
        maybeMap(actorFields.identityField, 'props.subjectRef', 'external_ref', true),
        maybeMap(dimension, 'props.dimension', 'payload', true),
        maybeMap(observedValue, 'props.observedValue', 'payload', true),
        maybeMap(scaleId, 'props.scaleId', 'provenance', true),
        maybeMap(scaleVersion, 'props.scaleVersion', 'provenance', true),
        maybeMap(cycleId, 'props.cycleId', 'provenance', true),
        maybeMap(assessedAt, 'props.observedAt', 'date_start'),
        maybeMap(expiresAt, 'props.expiresAt', 'date_end'),
      ]), 'tombstone'),
    ],
    identityRules: compact([
      actorFields.identityField ? { id: 'subject-identity', entityRef: 'subject', strategy: actorFields.identityStrategy, fields: [actorFields.identityField], targetProfileId: 'orgchart' } as const : null,
      observationId ? { id: 'observation-identity', entityRef: 'observation', strategy: 'external_ref', fields: [observationId], targetProfileId: 'orgchart' } as const : null,
    ]),
  });
}

function buildSuccessionPlanningProfile(input: BuildProfileInput): SourceMappingProfile {
  const positionId = field(input.matches, 'positionId');
  const personId = field(input.matches, 'personId');
  const person = field(input.matches, 'person');
  const successorId = field(input.matches, 'successorId');
  const successor = field(input.matches, 'successor');
  const readiness = field(input.matches, 'readiness');
  const rank = field(input.matches, 'rank');
  const sourcePersonIdentityField = personId ?? person;
  const successorIdentityField = successorId ?? successor;
  const readyNowColumn = successor
    ? normalize(successor) === normalize('ready now successor')
    : false;
  return profile(input, {
    // Restricted nominations must always pass the explicit mapping review,
    // even when the header match itself is high confidence.
    requiresReview: true,
    retentionClass: 'audit_retained',
    entityMappings: compact([
      sourcePersonIdentityField ? {
        ...entity('source-person', 'actor', 'orgchart', compact([
          maybeMap(person, 'props.name', 'label', !personId),
          maybeMap(personId, 'props.employeeId', 'external_ref', true),
        ]), 'retain'),
        optionalWhenBlank: true,
        referenceOnly: true,
      } : null,
      {
        ...entity('successor', 'actor', 'orgchart', compact([
          maybeMap(successor, 'props.name', 'label', !successorId),
          maybeMap(successorId, 'props.employeeId', 'external_ref', true),
        ]), 'review'),
        optionalWhenBlank: true,
        referenceOnly: true,
      },
      {
        ...entity('target-position', 'position', 'orgchart', compact([
          maybeMap(positionId, 'props.positionId', 'external_ref', true),
          maybeMap(positionId, 'props.name', 'label'),
        ]), 'retain'),
        referenceOnly: true,
      },
    ]),
    relationMappings: [{
      id: 'successor-nomination',
      predicate: 'successor_of',
      sourceEntityRef: 'successor',
      targetEntityRef: 'target-position',
      identity: 'endpoints',
      optionalSource: true,
      qualifierMappings: compact([
        maybeMap(readiness, 'qualifiers.readiness', 'payload'),
        maybeMap(rank, 'qualifiers.rank', 'payload'),
      ]),
      defaultQualifiers: readyNowColumn ? { readiness: 'ready_now' } : {},
      // A complete source snapshot may propose retirement, but a sticky human
      // decision must be reviewed rather than silently deleted.
      tombstonePolicy: 'review',
    }],
    identityRules: compact([
      sourcePersonIdentityField ? {
        id: 'source-person-identity', entityRef: 'source-person', identityNamespace: 'person',
        strategy: personId ? 'external_ref' : 'label_match',
        fields: [sourcePersonIdentityField], targetProfileId: 'orgchart',
        ...(personId ? {} : { confidence: 0.65 }),
      } as const : null,
      successorIdentityField ? {
        id: 'successor-identity', entityRef: 'successor', identityNamespace: 'person',
        strategy: successorId ? 'external_ref' : 'label_match',
        fields: [successorIdentityField], targetProfileId: 'orgchart',
        ...(successorId ? {} : { confidence: 0.65 }),
      } as const : null,
      positionId ? {
        id: 'target-position-identity', entityRef: 'target-position', identityNamespace: 'position',
        strategy: 'external_ref', fields: [positionId], targetProfileId: 'orgchart',
      } as const : null,
    ]),
  });
}

function buildTaskTrackerProfile(input: BuildProfileInput): SourceMappingProfile {
  const issueField = field(input.matches, 'issue');
  const summaryField = field(input.matches, 'summary');
  const statusField = field(input.matches, 'status');
  const assigneeField = field(input.matches, 'assignee');
  const assigneeFields = actorIdentityFields(input, assigneeField);
  const sprintField = field(input.matches, 'sprint');
  const epicField = field(input.matches, 'epic');
  return profile(input, {
    entityMappings: [
      entity('task', 'activity', 'gantt', compact([
        maybeMap(issueField, 'props.externalRef.key', 'external_ref', true),
        maybeMap(summaryField, 'props.name', 'label', true),
        maybeMap(statusField, 'props.status', 'status'),
        maybeMap(sprintField, 'props.sprint', 'provenance'),
        maybeMap(epicField, 'props.epic', 'provenance'),
      ])),
      entity('assignee', 'actor', 'orgchart', compact([
        maybeMap(assigneeFields.labelField ?? assigneeFields.identityField, 'props.name', 'label'),
        maybeMap(assigneeFields.emailField, 'props.email', 'external_ref'),
      ])),
    ],
    relationMappings: assigneeFields.identityField || assigneeFields.labelField
      ? [{
          id: 'task-assignee',
          predicate: 'responsible_for',
          sourceEntityRef: 'assignee',
          targetEntityRef: 'task',
          qualifierMappings: [],
          defaultQualifiers: { basis: 'source_assignee' },
        }]
      : [],
    identityRules: compact([
      issueField
        ? {
            id: 'task-external-ref',
            entityRef: 'task',
            strategy: 'external_ref',
            fields: [issueField],
          } as const
        : null,
      assigneeFields.identityField
        ? {
            id: 'assignee-identity',
            entityRef: 'assignee',
            strategy: assigneeFields.identityStrategy,
            fields: [assigneeFields.identityField],
            targetProfileId: 'orgchart',
          } as const
        : null,
      assigneeFields.identityField && assigneeFields.labelField &&
        assigneeFields.identityField !== assigneeFields.labelField
        ? {
            id: 'assignee-label',
            entityRef: 'assignee',
            strategy: 'label_match',
            fields: [assigneeFields.labelField],
            targetProfileId: 'orgchart',
            confidence: 0.65,
          } as const
        : null,
    ]),
  });
}

function buildProcessRaciProfile(input: BuildProfileInput): SourceMappingProfile {
  const processField = field(input.matches, 'process');
  const activityField = field(input.matches, 'activity');
  const actorField = field(input.matches, 'actor');
  const raciField = field(input.matches, 'raci');
  return profile(input, {
    entityMappings: [
      entity('activity', 'activity', 'process', compact([
        maybeMap(activityField ?? processField, 'props.name', 'label', true),
        maybeMap(processField, 'props.processName', 'provenance'),
      ])),
      entity('actor', 'actor', 'orgchart', compact([
        maybeMap(actorField, 'props.name', 'label', true),
      ])),
    ],
    relationMappings: actorField
      ? [{
          id: 'raci-assignment',
          predicate: 'responsible_for',
          sourceEntityRef: 'actor',
          targetEntityRef: 'activity',
          qualifierMappings: compact([
            maybeMap(raciField, 'qualifiers.assignmentType', 'predicate'),
          ]),
          defaultQualifiers: { basis: 'source_raci' },
        }]
      : [],
    identityRules: compact([
      activityField
        ? {
            id: 'activity-identity',
            entityRef: 'activity',
            strategy: 'label_match',
            fields: [activityField],
          } as const
        : null,
      actorField
        ? {
            id: 'actor-identity',
            entityRef: 'actor',
            strategy: 'label_match',
            fields: [actorField],
            targetProfileId: 'orgchart',
          } as const
        : null,
    ]),
  });
}

function buildPeopleProfile(input: BuildProfileInput): SourceMappingProfile {
  const personField = field(input.matches, 'person');
  const emailField = field(input.matches, 'email');
  const titleField = field(input.matches, 'title');
  const departmentField = field(input.matches, 'department');
  return profile(input, {
    entityMappings: [
      entity('person', 'actor', 'orgchart', compact([
        maybeMap(personField, 'props.name', 'label', true),
        maybeMap(emailField, 'props.email', 'external_ref'),
        maybeMap(titleField, 'props.title', 'payload'),
        maybeMap(departmentField, 'props.department', 'payload'),
      ])),
    ],
    identityRules: compact([
      emailField
        ? {
            id: 'person-email',
            entityRef: 'person',
            strategy: 'external_ref',
            fields: [emailField],
          } as const
        : personField
          ? {
              id: 'person-name',
              entityRef: 'person',
              strategy: 'label_match',
              fields: [personField],
            } as const
          : null,
    ]),
  });
}

function buildPersonHierarchyProfile(input: BuildProfileInput): SourceMappingProfile {
  const matchedPersonField = field(input.matches, 'person');
  const explicitPersonIdField = field(input.matches, 'personId') ?? field(input.matches, 'email');
  const personIdField = explicitPersonIdField ?? matchedPersonField;
  const personField = matchedPersonField && matchedPersonField !== personIdField
    ? matchedPersonField
    : findHeaderExcluding(input.headers, FIELD_PATTERNS.person, [personIdField]);
  const managerField = field(input.matches, 'manager');
  const explicitManagerIdField = distinctManagerField(
    field(input.matches, 'managerId'),
    personIdField,
  );
  const managerIdField = explicitManagerIdField ?? managerField;
  const matchedSecondaryManagerField = field(input.matches, 'secondaryManager');
  const explicitSecondaryManagerIdField = distinctManagerField(
    field(input.matches, 'secondaryManagerId'),
    personIdField,
  );
  const secondaryManagerIdField = explicitSecondaryManagerIdField
    ?? matchedSecondaryManagerField;
  const secondaryManagerField = matchedSecondaryManagerField
    ?? explicitSecondaryManagerIdField;
  const titleField = field(input.matches, 'title');
  const departmentField = field(input.matches, 'department');
  const positionIdField = field(input.matches, 'positionId');
  const managerPositionIdField = distinctManagerField(
    field(input.matches, 'managerPositionId'),
    positionIdField,
  );
  const managerPositionIdentityField = managerPositionIdField ?? managerIdField;
  // A derived position is keyed by the person occupying it, so that key has to
  // be the same attribute the manager column names. A sheet that identifies
  // employees by id but managers by email would otherwise key positions by id
  // and manager-positions by email, and a manager reference could never unify
  // with that manager's own position — every report would hang off a phantom.
  // This intent folds the email patterns into its `personId` group, so the
  // person's own email is only reachable by matching the headers directly.
  const personEmailField = findHeaderExcluding(input.headers, FIELD_PATTERNS.email, [
    managerPositionIdentityField,
    managerIdField,
    managerField,
  ]);
  const personIdentityForPosition =
    headerLooksLikeEmail(managerPositionIdentityField)
    && !headerLooksLikeEmail(personIdField)
    && personEmailField
      ? personEmailField
      : personIdField;
  const positionIdentityField = positionIdField ?? personIdentityForPosition;
  return profile(input, {
    entityMappings: compact([
      entity('person', 'actor', 'orgchart', compact([
        maybeMap(personField, 'props.name', 'label', true),
        maybeMap(personIdField, 'props.employeeId', 'external_ref'),
        // The person's own email is the anchor the reviewed identity connection
        // ("Connect workspace user to org person") matches a workspace member
        // against. `personEmailField` was computed above only to key derived
        // positions, so an imported chart carried names and titles but no email
        // at all — and the connect search could never resolve anyone, however
        // the reviewer spelled the query. `email` is a registered orgchart-base
        // field at `internal` sensitivity, so it stays in raw universalProps
        // and remains searchable rather than moving to protected storage.
        //
        // Re-check the header before writing it: `findHeader` also matches a
        // pattern that *contains* the header, so the "user principal name"
        // pattern claims a plain `name` column on a sheet with no email at all.
        // That was harmless while the value only chose a position key; writing
        // it to props.email would file every person's name as their address.
        maybeMap(
          headerLooksLikeEmail(personEmailField) ? personEmailField : null,
          'props.email',
          'payload',
        ),
        maybeMap(titleField, 'props.title', 'payload'),
        maybeMap(departmentField, 'props.department', 'payload'),
        maybeMap(field(input.matches, 'departureDate'), 'props.departureDate', 'payload'),
      ]), 'tombstone'),
      managerField ? {
        ...entity('manager', 'actor', 'orgchart', compact([
          maybeMap(managerField, 'props.name', 'label', true),
          maybeMap(managerIdField, 'props.employeeId', 'external_ref'),
        ]), 'tombstone'),
        optionalWhenBlank: true,
      } : null,
      secondaryManagerField ? {
        ...entity('secondary-manager', 'actor', 'orgchart', compact([
          maybeMap(secondaryManagerField, 'props.name', 'label', true),
          maybeMap(secondaryManagerIdField, 'props.employeeId', 'external_ref'),
        ]), 'tombstone'),
        optionalWhenBlank: true,
      } : null,
      entity('position', 'position', 'orgchart', compact([
        maybeMap(titleField, 'props.name', 'label', true),
        maybeMap(titleField, 'props.title', 'payload'),
        maybeMap(positionIdField, 'props.positionId', 'external_ref'),
        maybeMap(personIdField, 'props.derivedFromPersonRef', 'provenance'),
        maybeMap(departmentField, 'props.department', 'payload'),
      ]), positionIdField ? 'retain' : 'review'),
      managerField ? {
        ...entity('manager-position', 'position', 'orgchart', compact([
          maybeMap(managerField, 'props.name', 'label', true),
          maybeMap(managerPositionIdField, 'props.positionId', 'external_ref'),
          maybeMap(managerIdField, 'props.derivedFromPersonRef', 'provenance'),
        ]), managerPositionIdField ? 'retain' : 'review'),
        optionalWhenBlank: true,
      } : null,
      secondaryManagerField ? {
        ...entity('secondary-manager-position', 'position', 'orgchart', compact([
          maybeMap(secondaryManagerField, 'props.name', 'label', true),
          maybeMap(secondaryManagerIdField, 'props.derivedFromPersonRef', 'provenance'),
        ]), 'review'),
        optionalWhenBlank: true,
      } : null,
      departmentField ? entity('department', 'department', 'orgchart', compact([
        maybeMap(departmentField, 'props.name', 'label', true),
      ]), 'retain') : null,
    ]),
    relationMappings: compact([
      {
        id: 'occupies',
        predicate: 'occupies',
        sourceEntityRef: 'person',
        targetEntityRef: 'position',
        qualifierMappings: [],
        defaultQualifiers: { assignmentType: 'primary', basis: 'derived_person_hierarchy' },
        tombstonePolicy: 'tombstone',
      } as const,
      managerField ? {
        id: 'reports-to',
        predicate: 'reports_to',
        sourceEntityRef: 'position',
        targetEntityRef: 'manager-position',
        optionalTarget: true,
        qualifierMappings: [],
        defaultQualifiers: { basis: 'derived_person_hierarchy' },
        tombstonePolicy: 'tombstone',
      } as const : null,
      secondaryManagerField ? {
        id: 'matrix-reports-to',
        predicate: 'matrix_reports_to',
        sourceEntityRef: 'position',
        targetEntityRef: 'secondary-manager-position',
        optionalTarget: true,
        qualifierMappings: [],
        defaultQualifiers: { basis: 'derived_person_hierarchy' },
        tombstonePolicy: 'tombstone',
      } as const : null,
    ]),
    identityRules: compact([
      personIdField
        ? {
            id: 'person-identity',
            entityRef: 'person',
            identityNamespace: 'person',
            strategy: explicitPersonIdField ? 'external_ref' : 'label_match',
            fields: [personIdField],
          } as const
        : null,
      managerIdField
        ? {
            id: 'manager-identity',
            entityRef: 'manager',
            identityNamespace: 'person',
            strategy: explicitManagerIdField ? 'external_ref' : 'label_match',
            fields: [managerIdField],
          } as const
        : null,
      secondaryManagerIdField
        ? {
            id: 'secondary-manager-identity',
            entityRef: 'secondary-manager',
            identityNamespace: 'person',
            strategy: explicitSecondaryManagerIdField ? 'external_ref' : 'label_match',
            fields: [secondaryManagerIdField],
          } as const
        : null,
      positionIdentityField ? {
        id: 'derived-position-identity',
        entityRef: 'position',
        identityNamespace: 'position',
        strategy: positionIdField ? 'external_ref' : 'manual_review',
        fields: [positionIdentityField],
      } as const : null,
      managerPositionIdentityField ? {
        id: 'derived-manager-position-identity',
        entityRef: 'manager-position',
        identityNamespace: 'position',
        strategy: managerPositionIdField ? 'external_ref' : 'manual_review',
        fields: [managerPositionIdentityField],
      } as const : null,
      secondaryManagerIdField ? {
        id: 'derived-secondary-manager-position-identity',
        entityRef: 'secondary-manager-position',
        identityNamespace: 'position',
        strategy: explicitSecondaryManagerIdField ? 'external_ref' : 'manual_review',
        fields: [secondaryManagerIdField],
      } as const : null,
      departmentField ? {
        id: 'department-identity',
        entityRef: 'department',
        identityNamespace: 'department',
        strategy: 'label_match',
        fields: [departmentField],
      } as const : null,
    ]),
  });
}

function buildPositionHierarchyProfile(input: BuildProfileInput): SourceMappingProfile {
  const positionId = field(input.matches, 'positionId');
  const managerPositionId = distinctManagerField(
    field(input.matches, 'managerPositionId'),
    positionId,
  );
  const title = field(input.matches, 'title');
  const matchedPerson = field(input.matches, 'person');
  const explicitPersonId = field(input.matches, 'personId');
  const personId = explicitPersonId ?? matchedPerson;
  const person = matchedPerson && matchedPerson !== personId
    ? matchedPerson
    : findHeaderExcluding(input.headers, FIELD_PATTERNS.person, [personId]);
  const department = field(input.matches, 'department');
  // A person-named manager only applies when no manager position column
  // exists; the seat reference is the more direct fact and wins.
  const explicitManagerId = distinctManagerField(field(input.matches, 'managerId'), personId);
  const matchedManager = distinctManagerField(field(input.matches, 'manager'), person);
  const managerPersonId = managerPositionId ? null : (explicitManagerId ?? matchedManager);
  const managerPersonLabel = managerPersonId && matchedManager && matchedManager !== managerPersonId
    ? matchedManager
    : null;
  return profile(input, {
    entityMappings: compact([
      entity('position', 'position', 'orgchart', compact([
        maybeMap(title, 'props.name', 'label'),
        maybeMap(title, 'props.title', 'payload'),
        maybeMap(positionId, 'props.positionId', 'external_ref', true),
        maybeMap(department, 'props.department', 'payload'),
      ]), 'tombstone'),
      managerPositionId ? {
        ...entity('manager-position', 'position', 'orgchart', compact([
          maybeMap(managerPositionId, 'props.name', 'label', true),
          maybeMap(managerPositionId, 'props.positionId', 'external_ref', true),
        ]), 'tombstone'),
        // A hierarchy root has no manager position by definition.
        optionalWhenBlank: true,
      } : null,
      // The incumbent must precede the manager mappings: the first mapping in
      // a namespace is its primary, and manager references resolve against it.
      person ? {
        ...entity('person', 'actor', 'orgchart', compact([
          maybeMap(person, 'props.name', 'label', true),
          maybeMap(personId, 'props.employeeId', 'external_ref'),
        ]), 'review'),
        // Position-keyed sheets must retain vacant seats as rows with blank
        // incumbent cells after a transfer or departure.
        optionalWhenBlank: true,
      } : null,
      managerPersonId ? {
        ...entity('manager', 'actor', 'orgchart', compact([
          maybeMap(managerPersonLabel ?? managerPersonId, 'props.name', 'label', true),
          maybeMap(managerPersonId, 'props.employeeId', 'external_ref'),
        ]), 'review'),
        optionalWhenBlank: true,
      } : null,
      managerPersonId ? {
        // The manager's seat, resolved through the manager's own row: the
        // person reference shares its identity signature with the `manager`
        // rule, so the resolver adopts that row's position instead of
        // minting a seat named after an employee id.
        ...entity('manager-position', 'position', 'orgchart', compact([
          maybeMap(managerPersonLabel ?? managerPersonId, 'props.name', 'label', true),
          maybeMap(managerPersonId, 'props.derivedFromPersonRef', 'provenance'),
        ]), 'review'),
        optionalWhenBlank: true,
      } : null,
      department ? entity('department', 'department', 'orgchart', compact([
        maybeMap(department, 'props.name', 'label', true),
      ]), 'retain') : null,
    ]),
    relationMappings: compact([
      managerPositionId || managerPersonId ? {
        id: 'reports-to',
        predicate: 'reports_to',
        sourceEntityRef: 'position',
        targetEntityRef: 'manager-position',
        optionalTarget: true,
        qualifierMappings: [],
        defaultQualifiers: { basis: 'source_position_hierarchy' },
        tombstonePolicy: 'tombstone',
      } as const : null,
      person ? {
        id: 'occupies',
        predicate: 'occupies',
        sourceEntityRef: 'person',
        targetEntityRef: 'position',
        optionalSource: true,
        qualifierMappings: [],
        defaultQualifiers: { assignmentType: 'primary', basis: 'source_position_hierarchy' },
        tombstonePolicy: 'review',
      } as const : null,
    ]),
    identityRules: compact([
      positionId ? {
        id: 'position-identity', entityRef: 'position', identityNamespace: 'position',
        strategy: 'external_ref', fields: [positionId],
      } as const : null,
      managerPositionId ? {
        id: 'manager-position-identity', entityRef: 'manager-position', identityNamespace: 'position',
        strategy: 'external_ref', fields: [managerPositionId],
      } as const : null,
      managerPersonId ? {
        id: 'manager-identity', entityRef: 'manager', identityNamespace: 'person',
        strategy: explicitManagerId ? 'external_ref' : 'label_match', fields: [managerPersonId],
      } as const : null,
      managerPersonId ? {
        id: 'manager-position-identity', entityRef: 'manager-position', identityNamespace: 'position',
        strategy: 'manual_review', fields: [managerPersonId],
      } as const : null,
      personId ? {
        id: 'person-identity', entityRef: 'person', identityNamespace: 'person',
        strategy: explicitPersonId ? 'external_ref' : 'label_match', fields: [personId],
      } as const : null,
      department ? {
        id: 'department-identity', entityRef: 'department', identityNamespace: 'department',
        strategy: 'label_match', fields: [department],
      } as const : null,
    ]),
  });
}

function buildControlRiskProfile(input: BuildProfileInput): SourceMappingProfile {
  const controlField = field(input.matches, 'control');
  const riskField = field(input.matches, 'risk');
  const frameworkField = field(input.matches, 'framework');
  const evidenceField = field(input.matches, 'evidence');
  const findingField = field(input.matches, 'finding');
  return profile(input, {
    entityMappings: [
      entity('control', 'control', 'grc-control', compact([
        maybeMap(controlField, 'props.name', 'label', true),
        maybeMap(frameworkField, 'props.frameworkRef', 'external_ref'),
        maybeMap(evidenceField, 'props.evidence', 'provenance'),
        maybeMap(findingField, 'props.finding', 'payload'),
      ])),
      entity('risk', 'risk', 'grc-control', compact([
        maybeMap(riskField, 'props.name', 'label'),
      ])),
    ],
    relationMappings: riskField
      ? [{
          id: 'control-risk',
          predicate: 'mitigates',
          sourceEntityRef: 'control',
          targetEntityRef: 'risk',
          qualifierMappings: [],
          defaultQualifiers: { basis: 'source_register' },
        }]
      : [],
    identityRules: compact([
      controlField
        ? {
            id: 'control-identity',
            entityRef: 'control',
            strategy: 'external_ref',
            fields: [controlField],
          } as const
        : null,
    ]),
  });
}

function buildAccessGrantProfile(input: BuildProfileInput): SourceMappingProfile {
  const actorField = field(input.matches, 'actor');
  const actorFields = actorIdentityFields(input, actorField);
  const appField = field(input.matches, 'application');
  const permissionField = field(input.matches, 'permission');
  const groupField = field(input.matches, 'group');
  return profile(input, {
    requiresReview: true,
    entityMappings: [
      entity('actor', 'actor', 'orgchart', compact([
        maybeMap(actorFields.labelField ?? actorFields.identityField, 'props.name', 'label', true),
        maybeMap(actorFields.emailField, 'props.email', 'external_ref'),
      ])),
      entity('resource', 'access_grant', 'grc-control', compact([
        maybeMap(appField, 'props.application', 'relationship_target', true),
        maybeMap(permissionField, 'props.permission', 'payload', true),
        maybeMap(groupField, 'props.group', 'payload'),
      ])),
    ],
    relationMappings: [{
      id: 'access-grant',
      predicate: 'granted_to',
      sourceEntityRef: 'resource',
      targetEntityRef: 'actor',
      qualifierMappings: compact([
        maybeMap(permissionField, 'qualifiers.permission', 'payload'),
        maybeMap(groupField, 'qualifiers.group', 'payload'),
      ]),
      defaultQualifiers: { basis: 'source_access_export' },
    }],
    identityRules: compact([
      actorFields.identityField
        ? {
            id: 'actor-identity',
            entityRef: 'actor',
            strategy: actorFields.identityStrategy,
            fields: [actorFields.identityField],
            targetProfileId: 'orgchart',
          } as const
        : null,
      actorFields.identityField && actorFields.labelField &&
        actorFields.identityField !== actorFields.labelField
        ? {
            id: 'actor-label',
            entityRef: 'actor',
            strategy: 'label_match',
            fields: [actorFields.labelField],
            targetProfileId: 'orgchart',
            confidence: 0.65,
          } as const
        : null,
    ]),
  });
}

function buildSystemInventoryProfile(input: BuildProfileInput): SourceMappingProfile {
  const resourceField = field(input.matches, 'resource');
  const serviceField = field(input.matches, 'service');
  const appField = field(input.matches, 'application');
  return profile(input, {
    entityMappings: [
      entity('resource', 'system', 'network', compact([
        maybeMap(resourceField ?? appField, 'props.resourceId', 'external_ref', true),
        maybeMap(serviceField, 'props.service', 'payload'),
      ])),
    ],
    identityRules: compact([
      resourceField
        ? {
            id: 'resource-id',
            entityRef: 'resource',
            strategy: 'external_ref',
            fields: [resourceField],
          } as const
        : null,
    ]),
  });
}

function buildSoftwareDesignProfile(input: BuildProfileInput): SourceMappingProfile {
  const componentField = field(input.matches, 'component');
  const databaseField = field(input.matches, 'database');
  const serviceField = field(input.matches, 'service');
  return profile(input, {
    entityMappings: [
      entity('component', 'component', 'component', compact([
        maybeMap(componentField ?? serviceField, 'props.name', 'label', true),
        maybeMap(databaseField, 'props.database', 'payload'),
      ])),
    ],
    identityRules: compact([
      componentField
        ? {
            id: 'component-name',
            entityRef: 'component',
            strategy: 'label_match',
            fields: [componentField],
          } as const
        : null,
    ]),
  });
}

function buildProcessInventoryProfile(input: BuildProfileInput): SourceMappingProfile {
  const processIdField = field(input.matches, 'processId');
  const nameField = field(input.matches, 'name');
  const activityField = field(input.matches, 'activity');
  const actorField = field(input.matches, 'actor');
  const departmentField = field(input.matches, 'department');
  const frequencyField = field(input.matches, 'frequency');
  const durationField = field(input.matches, 'duration');
  const criticalityField = field(input.matches, 'criticality');
  const statusField = field(input.matches, 'status');
  const descriptionField = field(input.matches, 'description');
  const controlsField = field(input.matches, 'controls');
  const systemField = field(input.matches, 'system');
  return profile(input, {
    entityMappings: [
      entity('process', 'activity', 'process', compact([
        maybeMap(processIdField, 'props.processId', 'external_ref', true),
        maybeMap(nameField ?? activityField, 'props.name', 'label', true),
        maybeMap(activityField, 'props.activity', 'payload'),
        maybeMap(departmentField, 'props.department', 'payload'),
        maybeMap(frequencyField, 'props.frequency', 'payload'),
        maybeMap(durationField, 'props.duration', 'payload'),
        maybeMap(criticalityField, 'props.criticality', 'payload'),
        maybeMap(statusField, 'props.status', 'payload'),
        maybeMap(descriptionField, 'props.description', 'payload'),
        maybeMap(controlsField, 'props.relatedControls', 'payload'),
        maybeMap(systemField, 'props.system', 'payload'),
      ])),
      entity('owner', 'actor', 'orgchart', compact([
        maybeMap(actorField, 'props.name', 'label'),
      ])),
    ],
    relationMappings: actorField
      ? [{
          id: 'process-owner',
          predicate: 'responsible_for',
          sourceEntityRef: 'owner',
          targetEntityRef: 'process',
          qualifierMappings: [],
          defaultQualifiers: { basis: 'source_owner' },
        }]
      : [],
    identityRules: compact([
      processIdField
        ? {
            id: 'process-id',
            entityRef: 'process',
            strategy: 'external_ref',
            fields: [processIdField],
          } as const
        : nameField
          ? {
              id: 'process-name',
              entityRef: 'process',
              strategy: 'label_match',
              fields: [nameField],
            } as const
          : null,
    ]),
  });
}

function buildGenericCandidate(
  input: SpreadsheetIntentResolverInput,
  sourceSystem: string,
): SpreadsheetIntentCandidate {
  const targetFolderKind = input.folderKind ?? 'workspace';
  const targetProfileId = input.profileId ?? targetFolderKind;
  const mappingProfile = SourceMappingProfileSchema.parse({
    id: 'spreadsheet:generic_table',
    sourceSystem,
    intent: 'generic_table',
    targetFolderKind,
    targetProfileId,
    entityMappings: [{
      id: 'row',
      sourceObject: 'row',
      ontologyType: 'artifact',
      profileId: targetProfileId,
      fieldMappings: input.headers.map((header) => ({
        sourceField: header,
        targetPath: `props.${safePath(header)}`,
        role: 'payload',
        required: false,
      })),
    }],
    relationMappings: [],
    identityRules: [],
    evidencePolicy: { mode: 'row_snapshot', includeFields: [...input.headers] },
    retentionClass: 'current_only',
    requiresReview: true,
    confidence: 0.2,
  });
  return {
    intent: 'generic_table',
    label: 'Generic table',
    confidence: 0.2,
    targetFolderKind,
    targetProfileId,
    reasons: ['No strong ontology pattern detected'],
    missingRecommendedFields: [],
    reviewFields: [],
    registeredFields: [],
    availableRegisteredFields: [],
    preservedFields: [],
    registeredFieldEntityRefs: {},
    unrecognizedHeaders: [],
    invariantViolations: [],
    mappingProfile,
  };
}

const POSITION_HIERARCHY_REPORTING_REQUIRED =
  'Map manager position ID, manager ID, or manager before importing a position hierarchy; without one the chart has no reporting lines.';

const REPORTING_GROUP_IDS = new Set(['managerPositionId', 'managerId', 'manager']);

const POSITION_HIERARCHY_PERSON_KEYED_BY_SEAT =
  'Incumbent ID uses the same column as position ID, which would create one person per seat. Map incumbent ID to a person key such as employee number, or leave it unmapped to key people by name.';

/**
 * A seat-per-row sheet keys seats and people differently: the same person
 * holds several rows. Keying the incumbent by the seat column silently splits
 * one human into one record per role, which the chart then shows as separate
 * people.
 */
function personKeyedBySeat(matches: readonly HeaderMatch[]): boolean {
  const positionId = field(matches, 'positionId');
  const personId = field(matches, 'personId');
  return Boolean(positionId && personId && normalize(positionId) === normalize(personId));
}

function hasReportingMapping(matches: readonly HeaderMatch[]): boolean {
  return matches.some((match) => REPORTING_GROUP_IDS.has(match.group.id) && Boolean(match.field));
}

/**
 * Reporting columns are consumed structurally by hierarchy intents. Offering
 * them as plain payload targets too invites mapping "reportsTo" as a stored
 * value and believing the hierarchy is covered when no edge is produced.
 */
const STRUCTURAL_REFERENCE_FIELDS = new Set(['reportsTo', 'secondaryReportsTo']);

function isHierarchyIntent(intent: SourceIntent): boolean {
  return intent === 'person_hierarchy'
    || intent === 'org_structure'
    || intent === 'position_hierarchy';
}

function supportsRegisteredFieldPreservation(intent: SourceIntent): boolean {
  return intent === 'people_directory'
    || intent === 'person_hierarchy'
    || intent === 'org_structure'
    || intent === 'position_hierarchy';
}

/**
 * Seat-per-row sources describe the position first: FTE, location or cost
 * centre on such a row belong to the seat, and the person only when no seat
 * mapping exists. People-first sources keep the person as the default and
 * only send fields that exist solely on positions to the derived seat.
 */
function preservedFieldEntityPreference(intent: SourceIntent): readonly string[] {
  return intent === 'position_hierarchy'
    ? ['position', DEFAULT_PRESERVED_FIELD_ENTITY]
    : [DEFAULT_PRESERVED_FIELD_ENTITY, 'position'];
}

function routeRegisteredFieldEntities(
  intent: SourceIntent,
  mappingProfile: SourceMappingProfile,
  targetFields: readonly string[],
  fieldEntityRefs: Readonly<Record<string, readonly string[]>> | undefined,
): Record<string, string> {
  const mappingIds = new Set(mappingProfile.entityMappings.map((mapping) => mapping.id));
  const preference = preservedFieldEntityPreference(intent);
  const routed: Record<string, string> = {};
  for (const targetField of targetFields) {
    const allowed = fieldEntityRefs?.[targetField];
    const chosen = allowed && allowed.length > 0
      ? preference.find((entityRef) => allowed.includes(entityRef) && mappingIds.has(entityRef))
      : undefined;
    routed[targetField] = chosen ?? DEFAULT_PRESERVED_FIELD_ENTITY;
  }
  return routed;
}

function resolvePreservedFields(
  headers: readonly NormalizedHeader[],
  importableFieldAliases:
    | Readonly<Record<string, readonly string[]>>
    | undefined,
): SpreadsheetPreservedField[] {
  if (!importableFieldAliases) return [];

  const fieldIdByExactName = new Map<string, string>();
  const fieldIdByAlias = new Map<string, string>();
  for (const [fieldId, aliases] of Object.entries(importableFieldAliases)) {
    fieldIdByExactName.set(normalize(fieldId), fieldId);
    for (const alias of aliases) {
      const normalizedAlias = normalize(alias);
      if (!fieldIdByAlias.has(normalizedAlias)) {
        fieldIdByAlias.set(normalizedAlias, fieldId);
      }
    }
  }

  const claimedTargets = new Set<string>();
  const preserved: SpreadsheetPreservedField[] = [];
  for (const header of headers) {
    const targetField = fieldIdByExactName.get(header.normalized)
      ?? fieldIdByAlias.get(header.normalized);
    if (!targetField || claimedTargets.has(targetField)) continue;
    claimedTargets.add(targetField);
    preserved.push({ sourceField: header.original, targetField });
  }
  return preserved;
}

function appendPreservedFields(
  mappingProfile: SourceMappingProfile,
  preservedFields: readonly SpreadsheetPreservedField[],
): SourceMappingProfile {
  if (preservedFields.length === 0) return mappingProfile;

  const additionsByMappingId = new Map<string, SourceFieldMapping[]>();
  const claimedTargetPaths = new Map<string, Set<string>>();
  for (const fieldMapping of preservedFields) {
    const entityMapping = preservedFieldEntityMapping(mappingProfile, fieldMapping);
    if (!entityMapping) continue;
    const claimed = claimedTargetPaths.get(entityMapping.id)
      ?? new Set(entityMapping.fieldMappings.map((mapping) => mapping.targetPath));
    claimedTargetPaths.set(entityMapping.id, claimed);
    const targetPath = `props.${fieldMapping.targetField}`;
    if (claimed.has(targetPath)) continue;
    claimed.add(targetPath);
    const additions = additionsByMappingId.get(entityMapping.id) ?? [];
    additions.push({
      sourceField: fieldMapping.sourceField,
      targetPath,
      role: 'payload',
      required: false,
      transform: undefined,
    });
    additionsByMappingId.set(entityMapping.id, additions);
  }
  if (additionsByMappingId.size === 0) return mappingProfile;

  return {
    ...mappingProfile,
    entityMappings: mappingProfile.entityMappings.map((mapping) => {
      const additions = additionsByMappingId.get(mapping.id);
      return additions
        ? { ...mapping, fieldMappings: [...mapping.fieldMappings, ...additions] }
        : mapping;
    }),
  };
}

function preservedFieldEntityMapping(
  mappingProfile: SourceMappingProfile,
  fieldMapping: SpreadsheetPreservedField,
): SourceEntityMapping | undefined {
  const entityRef = fieldMapping.entityRef ?? DEFAULT_PRESERVED_FIELD_ENTITY;
  return mappingProfile.entityMappings.find((mapping) => mapping.id === entityRef)
    ?? mappingProfile.entityMappings.find(
      (mapping) => mapping.id === DEFAULT_PRESERVED_FIELD_ENTITY,
    );
}

function preservedFieldsForProfile(
  mappingProfile: SourceMappingProfile,
  registeredFields: readonly SpreadsheetPreservedField[],
): SpreadsheetPreservedField[] {
  return registeredFields.filter((fieldMapping) => {
    const entityMapping = preservedFieldEntityMapping(mappingProfile, fieldMapping);
    if (!entityMapping) return false;
    return !entityMapping.fieldMappings.some(
      (mapping) => mapping.targetPath === `props.${fieldMapping.targetField}`,
    );
  });
}

/**
 * Structural checks on a built profile, independent of which column matcher
 * proposed it. A mapping can be individually plausible field by field and still
 * describe something impossible — most importantly an edge whose two endpoints
 * carry the same identity, which materializes as a self-loop and is only caught
 * much later by the structural preflight, surfaced to the user as a bare 500.
 *
 * Violations force the mapping review step rather than throwing: a wrong mapping
 * a human can correct is better than a failed import, and the same guard will
 * apply to any non-deterministic proposer added later.
 */
export function mappingProfileInvariantViolations(
  candidate: SourceMappingProfile,
): string[] {
  const identityByEntityRef = new Map(
    candidate.identityRules.map((rule) => [rule.entityRef, rule] as const),
  );
  const identityKey = (entityRef: string): string | null => {
    const rule = identityByEntityRef.get(entityRef);
    if (!rule || rule.fields.length === 0) return null;
    return `${rule.identityNamespace ?? entityRef}\u0000${JSON.stringify(rule.fields)}`;
  };

  const violations = new Set<string>();
  for (const relation of candidate.relationMappings) {
    const sourceKey = identityKey(relation.sourceEntityRef);
    const targetKey = identityKey(relation.targetEntityRef);
    if (sourceKey && targetKey && sourceKey === targetKey) {
      violations.add(`relation_endpoints_share_identity:${relation.id}`);
    }
  }
  // A position hierarchy with no reporting relation imports seats that all
  // float as roots. That is a mapping gap, not a shape of org, so it must go
  // through review where the reporting column can be picked.
  if (candidate.intent === 'position_hierarchy'
    && !candidate.relationMappings.some((relation) => relation.predicate === 'reports_to')) {
    violations.add('position_hierarchy_without_reporting');
  }
  if (candidate.intent === 'position_hierarchy') {
    const positionKey = identityKey('position');
    const personKey = identityKey('person');
    if (positionKey && personKey
      && positionKey.split('\u0000')[1] === personKey.split('\u0000')[1]) {
      violations.add('position_hierarchy_person_keyed_by_seat');
    }
  }
  return [...violations].sort();
}

function profile(
  input: BuildProfileInput,
  overrides: Partial<SourceMappingProfileInput>,
): SourceMappingProfile {
  const built = SourceMappingProfileSchema.parse({
    id: `spreadsheet:${input.spec.intent}`,
    sourceSystem: input.sourceSystem,
    intent: input.spec.intent,
    targetFolderKind: input.spec.targetFolderKind,
    targetProfileId: input.spec.targetProfileId,
    entityMappings: [],
    relationMappings: [],
    identityRules: [],
    evidencePolicy: { mode: input.spec.evidenceMode, includeFields: [] },
    retentionClass: input.spec.intent === 'control_risk_register' || input.spec.intent === 'access_grants'
      ? 'audit_retained'
      : 'current_only',
    requiresReview: input.confidence < AUTO_CONFIRM_MIN_CONFIDENCE,
    confidence: input.confidence,
    ...overrides,
  });
  if (built.requiresReview) return built;
  return mappingProfileInvariantViolations(built).length > 0
    ? SourceMappingProfileSchema.parse({ ...built, requiresReview: true })
    : built;
}

function entity(
  id: string,
  ontologyType: string,
  profileId: string,
  fieldMappings: SourceFieldMapping[],
  tombstonePolicy?: SourceTombstonePolicy,
): SourceEntityMapping {
  return {
    id,
    sourceObject: 'row',
    ontologyType,
    profileId,
    optionalWhenBlank: false,
    referenceOnly: false,
    fieldMappings,
    ...(tombstonePolicy ? { tombstonePolicy } : {}),
  };
}

function maybeMap(
  sourceField: string | null | undefined,
  targetPath: string,
  role: SourceFieldMapping['role'],
  required = false,
): SourceFieldMapping | null {
  if (!sourceField) return null;
  return { sourceField, targetPath, role, required, transform: undefined };
}

function group(
  id: string,
  label: string,
  patterns: readonly string[],
  weight: number,
  recommended = false,
): HeaderGroup {
  return { id, label, patterns, weight, recommended };
}

function exactGroup(
  id: string,
  label: string,
  patterns: readonly string[],
  weight: number,
  recommended = false,
): HeaderGroup {
  return { ...group(id, label, patterns, weight, recommended), exact: true };
}

interface NormalizedHeader {
  readonly original: string;
  readonly normalized: string;
}

interface ActorIdentityFields {
  readonly labelField: string | null;
  readonly emailField: string | null;
  readonly identityField: string | null;
  readonly identityStrategy: 'external_ref' | 'label_match';
}

function actorIdentityFields(
  input: BuildProfileInput,
  matchedActorField: string | null,
): ActorIdentityFields {
  const emailField = headerLooksLikeEmail(matchedActorField)
    ? matchedActorField
    : findActorEmailHeader(input.headers, [matchedActorField]);
  const labelField = matchedActorField && matchedActorField !== emailField
    ? matchedActorField
    : findHeaderExcluding(input.headers, FIELD_PATTERNS.person, [emailField]);
  const identityField = emailField ?? labelField ?? matchedActorField;
  return {
    labelField,
    emailField,
    identityField,
    identityStrategy: emailField ? 'external_ref' : 'label_match',
  };
}

function findActorEmailHeader(
  headers: readonly NormalizedHeader[],
  excluded: readonly (string | null | undefined)[] = [],
): string | null {
  const excludedValues = new Set(excluded
    .filter((value): value is string => !!value)
    .map(normalize));
  return headers.find((header) =>
    !excludedValues.has(header.normalized) &&
    headerLooksLikeEmail(header.original) &&
    isActorEmailHeader(header.normalized))?.original ?? null;
}

function isActorEmailHeader(normalizedHeader: string): boolean {
  const compactHeader = normalizedHeader.replace(/\s+/g, '');
  if ((DIRECT_EMAIL_HEADERS as readonly string[]).includes(compactHeader)) {
    return true;
  }
  return ACTOR_EMAIL_SUBJECT_HINTS.some((hint) => compactHeader.startsWith(hint));
}

function findHeaderExcluding(
  headers: readonly NormalizedHeader[],
  patterns: readonly string[],
  excluded: readonly (string | null | undefined)[] = [],
): string | null {
  const excludedValues = new Set(excluded
    .filter((value): value is string => !!value)
    .map(normalize));
  return findHeader(
    headers.filter((header) => !excludedValues.has(header.normalized)),
    patterns,
  );
}

/**
 * Drops a manager-side column that collapsed onto the employee-side column it
 * is supposed to point at. One cell cannot hold both a row's own key and its
 * manager's key, but fuzzy header matching can claim it for both: `findHeader`
 * also matches when a pattern contains the header, and the `managerId` pattern
 * "manager employee id" contains the header "Employee Id". A sheet with an
 * employee-id column and no manager-id column therefore handed the manager its
 * own row's identity, making every reports_to edge a self-loop and tripping the
 * structural preflight. Falling back to null lets the caller key the manager off
 * its label column instead.
 */
function distinctManagerField(
  managerSideField: string | null,
  subjectSideField: string | null,
): string | null {
  return managerSideField && managerSideField === subjectSideField
    ? null
    : managerSideField;
}

function headerLooksLikeEmail(fieldName: string | null | undefined): boolean {
  if (!fieldName) return false;
  const normalized = normalize(fieldName);
  return FIELD_PATTERNS.email.some((pattern) => {
    const normalizedPattern = normalize(pattern);
    return normalized === normalizedPattern ||
      normalized.includes(normalizedPattern);
  });
}

function normalizeHeaders(headers: readonly string[]): NormalizedHeader[] {
  return headers
    .map((header) => ({
      original: header.trim(),
      normalized: normalize(header),
    }))
    .filter((header) => header.original.length > 0);
}

/**
 * Match quality of a header against one pattern. The ordering matters more
 * than the numbers: a header that *is* the pattern beats a header that merely
 * contains it, which beats a header the pattern contains.
 *
 * That last kind is the dangerous one. It exists so a bare "Manager" header can
 * satisfy the pattern "line manager", but it also lets the `managerId` pattern
 * "manager employee id" swallow a plain "Employee Id" header — which keyed the
 * manager off the importing row and produced reports_to self-loops. Ranking the
 * kinds lets a weak claim lose to a strong one instead of racing it.
 */
const HEADER_MATCH_NONE = 0;
const HEADER_MATCH_PATTERN_CONTAINS_HEADER = 1;
const HEADER_MATCH_HEADER_CONTAINS_PATTERN = 2;
const HEADER_MATCH_EXACT = 3;

function headerMatchQuality(
  header: NormalizedHeader,
  patterns: readonly string[],
  exactOnly: boolean,
): number {
  let best = HEADER_MATCH_NONE;
  for (const pattern of patterns.map(normalize)) {
    if (header.normalized === pattern) return HEADER_MATCH_EXACT;
    if (exactOnly) continue;
    if (header.normalized.includes(pattern)) {
      best = Math.max(best, HEADER_MATCH_HEADER_CONTAINS_PATTERN);
      continue;
    }
    if (pattern.includes(header.normalized)) {
      best = Math.max(best, HEADER_MATCH_PATTERN_CONTAINS_HEADER);
    }
  }
  return best;
}

/**
 * Assigns headers to groups so no group wins a header that another group in the
 * same spec matches more strongly. Equal-quality claims are still shared, since
 * several builders legitimately read one column through more than one group and
 * disambiguate themselves. A group that loses its first choice falls through to
 * its next candidate rather than going empty.
 */
function matchGroupsToHeaders(
  groups: readonly HeaderGroup[],
  headers: readonly NormalizedHeader[],
): HeaderMatch[] {
  const bestQualityByHeader = new Map<string, number>();
  for (const header of headers) {
    let best = HEADER_MATCH_NONE;
    for (const candidateGroup of groups) {
      best = Math.max(
        best,
        headerMatchQuality(header, candidateGroup.patterns, candidateGroup.exact === true),
      );
    }
    bestQualityByHeader.set(header.normalized, best);
  }

  return groups.map((candidateGroup) => {
    const claimable = headers.filter((header) => {
      const quality = headerMatchQuality(
        header,
        candidateGroup.patterns,
        candidateGroup.exact === true,
      );
      return quality !== HEADER_MATCH_NONE &&
        quality >= (bestQualityByHeader.get(header.normalized) ?? HEADER_MATCH_NONE);
    });
    return {
      group: candidateGroup,
      // Pattern-preference order within the group is preserved by reusing the
      // existing finders over the narrowed header set.
      field: candidateGroup.exact
        ? findExactHeader(claimable, candidateGroup.patterns)
        : findHeader(claimable, candidateGroup.patterns),
    };
  });
}

function findHeader(
  headers: readonly NormalizedHeader[],
  patterns: readonly string[],
): string | null {
  for (const pattern of patterns.map(normalize)) {
    const exact = headers.find((header) => header.normalized === pattern);
    if (exact) return exact.original;
  }
  for (const pattern of patterns.map(normalize)) {
    const partial = headers.find((header) =>
      header.normalized.includes(pattern) || pattern.includes(header.normalized),
    );
    if (partial) return partial.original;
  }
  return null;
}

function findExactHeader(
  headers: readonly NormalizedHeader[],
  patterns: readonly string[],
): string | null {
  const normalizedPatterns = new Set(patterns.map(normalize));
  return headers.find((header) => normalizedPatterns.has(header.normalized))?.original ?? null;
}

function field(matches: readonly HeaderMatch[], id: string): string | null {
  return matches.find((match) => match.group.id === id)?.field ?? null;
}

function normalize(value: string): string {
  return value
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .replace(/[%]/g, ' percent ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

function safePath(header: string): string {
  return normalize(header).replace(/\s+/g, '_') || 'value';
}

function compact<T>(values: readonly (T | null | undefined)[]): T[] {
  return values.filter((value): value is T => value != null);
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, Number(value.toFixed(2))));
}
